import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router";
import { FaFileExcel, FaFileCsv } from "react-icons/fa6";
import ShowToast from "components/ShowToast";
import { useSession } from "hooks/useSession";
import { useAuth } from "hooks/useAuth";
import { fetchWrapper } from "utils/apiFetch";
import { exportDataToFile } from "utils/exportUtils";
import "./RegBank.css";

const BASE_URL = "https://pro.mokshiq.in";

const POPULAR_BANKS = [
  { code: "HDFC", name: "HDFC Bank", icon: "🏛️" },
  { code: "ICIC", name: "ICICI Bank", icon: "🏦" },
  { code: "SBIN", name: "State Bank of India", icon: "🏛️" },
  { code: "UTIB", name: "Axis Bank", icon: "🏦" },
  { code: "KKBK", name: "Kotak Mahindra Bank", icon: "🏢" },
  { code: "PUNB", name: "Punjab National Bank", icon: "🏛️" },
  { code: "BARB", name: "Bank of Baroda", icon: "🏦" },
  { code: "CNRB", name: "Canara Bank", icon: "🏛️" },
  { code: "YESB", name: "Yes Bank", icon: "🏢" },
  { code: "IDFB", name: "IDFC FIRST Bank", icon: "🏦" },
  { code: "INDB", name: "IndusInd Bank", icon: "🏛️" },
  { code: "UBIN", name: "Union Bank of India", icon: "🏦" },
];

const WALLET_TYPES = [

  { id: "main_wallet", label: "Main Wallet (Primary Balance)", icon: "💼" },
];

const PRESET_AMOUNTS = [1000, 5000, 10000, 25000, 50000, 100000];

export default function RegBank() {
  const location = useLocation();
  const { role: sessionRole, userid: sessionUserId } = useSession();
  const { user } = useAuth();
  const role = sessionRole || user?.role || localStorage.getItem("role") || "Retailer";
  const userId = sessionUserId || user?.id || localStorage.getItem("userid") || "";

  const roleLower = (role || "").trim().toLowerCase();
  const roleId = Number(localStorage.getItem("role_id") || user?.role_id || 0);

  const isAdmin =
    roleLower.includes("admin") ||
    roleLower === "white label" ||
    roleId === 1 ||
    roleId === 6;

  // Tabs for Admin: "admin_loads" (Admin Deposit Requests) | "admin_banks" (Bank Whitelist Requests) | "admin_holds" (Wallet Hold Management)
  // Tabs for User: "my_banks" | "load_fund" | "load_requests" | "provider_banks" | "my_holds"
  const getInitialTab = () => {
    if (isAdmin) {
      if (location.pathname.includes("bank_whitelist")) return "admin_banks";
      return "admin_loads";
    }
    return "my_banks";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [lastSyncTime, setLastSyncTime] = useState(new Date().toLocaleTimeString());
  const [isHardRefreshing, setIsHardRefreshing] = useState(false);

  // Keep activeTab synced if route or role switches
  useEffect(() => {
    if (isAdmin) {
      if (location.pathname.includes("bank_whitelist")) {
        setActiveTab("admin_banks");
      } else if (["my_banks", "submit_bank", "load_fund", "load_requests", "provider_banks", "my_holds"].includes(activeTab)) {
        setActiveTab("admin_loads");
      }
    } else {
      if (["admin_loads", "admin_banks", "admin_holds"].includes(activeTab)) {
        setActiveTab("my_banks");
      }
    }
  }, [isAdmin, activeTab, location.pathname]);

  // ─────────────────────────────────────────────────────────────
  // 1. User State — Whitelisted Banks & Load Requests
  // ─────────────────────────────────────────────────────────────
  const [bankList, setBankList] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankSearch, setBankSearch] = useState("");
  const [bankStatusFilter, setBankStatusFilter] = useState("all");
  const [submitBankModalOpen, setSubmitBankModalOpen] = useState(false);

  // User Load Requests Filter & Search State
  const [userLoadSearch, setUserLoadSearch] = useState("");
  const [userLoadStatusFilter, setUserLoadStatusFilter] = useState("all");

  // Submit Bank Form State
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccount, setConfirmAccount] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [chequeFile, setChequeFile] = useState(null);
  const [chequePreview, setChequePreview] = useState("");
  const [submittingBank, setSubmittingBank] = useState(false);
  const fileInputRef = useRef(null);

  // User Load Funds Form State
  const [loadBankId, setLoadBankId] = useState("");
  const [providerAccount, setProviderAccount] = useState("");
  const [providerAccounts, setProviderAccounts] = useState([]);
  const [loadingProviderAccounts, setLoadingProviderAccounts] = useState(false);
  const [providerSearch, setProviderSearch] = useState("");
  const [loadWalletType, setLoadWalletType] = useState("recharge_wallet");
  const [loadAmount, setLoadAmount] = useState("");
  const [loadUtr, setLoadUtr] = useState("");
  const [loadRemark, setLoadRemark] = useState("");
  const [depositSlipFile, setDepositSlipFile] = useState(null);
  const [depositSlipPreview, setDepositSlipPreview] = useState("");
  const [submittingLoad, setSubmittingLoad] = useState(false);
  const slipInputRef = useRef(null);

  // User Load Requests History
  const [loadRequests, setLoadRequests] = useState([]);
  const [loadingLoads, setLoadingLoads] = useState(false);

  // User Holds
  const [userHoldList, setUserHoldList] = useState([]);
  const [userCurrentHold, setUserCurrentHold] = useState(0);
  const [loadingUserHolds, setLoadingUserHolds] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // 2. Admin State — Bank Whitelist Review
  // ─────────────────────────────────────────────────────────────
  const [adminBankList, setAdminBankList] = useState([]);
  const [loadingAdminBanks, setLoadingAdminBanks] = useState(false);
  const [adminBankStatusFilter, setAdminBankStatusFilter] = useState("pending");
  const [adminBankSearch, setAdminBankSearch] = useState("");

  // Review Modal State (Bank Whitelist)
  const [reviewBankModal, setReviewBankModal] = useState(false);
  const [selectedBankItem, setSelectedBankItem] = useState(null);
  const [reviewDecision, setReviewDecision] = useState("approve"); // "approve" | "reject"
  const [reviewRemark, setReviewRemark] = useState("");
  const [reviewingBank, setReviewingBank] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // 3. Admin State — Load / Deposit Requests Review
  // ─────────────────────────────────────────────────────────────
  const [adminLoadList, setAdminLoadList] = useState([]);
  const [loadingAdminLoads, setLoadingAdminLoads] = useState(false);
  const [adminLoadStatusFilter, setAdminLoadStatusFilter] = useState("pending");
  const [adminLoadSearch, setAdminLoadSearch] = useState("");

  // Review Modal State (Load Request)
  const [reviewLoadModal, setReviewLoadModal] = useState(false);
  const [selectedLoadItem, setSelectedLoadItem] = useState(null);
  const [reviewLoadDecision, setReviewLoadDecision] = useState("approve");
  const [reviewLoadRemark, setReviewLoadRemark] = useState("");
  const [reviewingLoad, setReviewingLoad] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // 4. Admin State — Wallet Hold & Release Management
  // ─────────────────────────────────────────────────────────────
  const [adminHoldList, setAdminHoldList] = useState([]);
  const [adminCurrentHold, setAdminCurrentHold] = useState(0);
  const [loadingAdminHolds, setLoadingAdminHolds] = useState(false);
  const [holdSearchUserId, setHoldSearchUserId] = useState("");

  // Hold / Unhold Action Modal
  const [holdActionModal, setHoldActionModal] = useState({
    open: false,
    type: "hold", // "hold" | "unhold"
    userId: "",
    userName: "",
    walletType: "main_wallet",
    amount: "",
    reason: "",
  });
  const [submittingHoldAction, setSubmittingHoldAction] = useState(false);

  // ─────────────────────────────────────────────────────────────
  // 5. Image Viewer Modal (Cheque / Slip)
  // ─────────────────────────────────────────────────────────────
  const [previewImageModal, setPreviewImageModal] = useState({
    open: false,
    title: "",
    url: "",
    subtitle: "",
  });

  // ─────────────────────────────────────────────────────────────
  // API Fetch Wrappers (Production-grade fetchWrapper with no-store cache)
  // ─────────────────────────────────────────────────────────────

  // 1. Fetch User Whitelisted Banks (GET /bank_whitelist_list)
  const fetchBankList = useCallback(async (silent = false) => {
    if (isAdmin) return;
    if (!silent) setLoadingBanks(true);
    try {
      const data = await fetchWrapper(`${BASE_URL}/bank_whitelist_list`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        setBankList(Array.isArray(data.data) ? data.data : []);
      } else {
        if (!silent) setBankList([]);
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn("fetchBankList error:", err);
      if (!silent) setBankList([]);
    } finally {
      if (!silent) setLoadingBanks(false);
    }
  }, [isAdmin]);

  // 2. Fetch Admin Bank Whitelist Requests (GET /bank_whitelist_admin_list)
  const fetchAdminBankList = useCallback(async (silent = false) => {
    if (!isAdmin) return;
    if (!silent) setLoadingAdminBanks(true);
    try {
      const statusParam = adminBankStatusFilter === "all" ? "" : `?status=${adminBankStatusFilter}`;
      const data = await fetchWrapper(`${BASE_URL}/bank_whitelist_admin_list${statusParam}`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        setAdminBankList(Array.isArray(data.data) ? data.data : []);
      } else {
        if (!silent) setAdminBankList([]);
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn("fetchAdminBankList error:", err);
      if (!silent) setAdminBankList([]);
    } finally {
      if (!silent) setLoadingAdminBanks(false);
    }
  }, [adminBankStatusFilter, isAdmin]);

  // 3. Fetch User Load Requests (GET /load_amount_list)
  const fetchLoadRequests = useCallback(async (silent = false) => {
    if (isAdmin) return;
    if (!silent) setLoadingLoads(true);
    try {
      const data = await fetchWrapper(`${BASE_URL}/load_amount_list`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        setLoadRequests(Array.isArray(data.data) ? data.data : []);
      } else {
        if (!silent) setLoadRequests([]);
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn("fetchLoadRequests error:", err);
      if (!silent) setLoadRequests([]);
    } finally {
      if (!silent) setLoadingLoads(false);
    }
  }, [isAdmin]);

  // 4. Fetch Admin Load Requests (GET /load_amount_admin_list)
  const fetchAdminLoadList = useCallback(async (silent = false) => {
    if (!isAdmin) return;
    if (!silent) setLoadingAdminLoads(true);
    try {
      const statusParam = adminLoadStatusFilter === "all" ? "" : `?status=${adminLoadStatusFilter}`;
      const data = await fetchWrapper(`${BASE_URL}/load_amount_admin_list${statusParam}`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        setAdminLoadList(Array.isArray(data.data) ? data.data : []);
      } else {
        if (!silent) setAdminLoadList([]);
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn("fetchAdminLoadList error:", err);
      if (!silent) setAdminLoadList([]);
    } finally {
      if (!silent) setLoadingAdminLoads(false);
    }
  }, [adminLoadStatusFilter, isAdmin]);

  // 5. Fetch User Own Holds (GET /load_amount_hold_list)
  const fetchUserHolds = useCallback(async (silent = false) => {
    if (isAdmin) return;
    if (!silent) setLoadingUserHolds(true);
    try {
      const data = await fetchWrapper(`${BASE_URL}/load_amount_hold_list`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        setUserHoldList(Array.isArray(data.data) ? data.data : []);
        setUserCurrentHold(data.current_hold || 0);
      } else {
        if (!silent) {
          setUserHoldList([]);
          setUserCurrentHold(0);
        }
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn("fetchUserHolds error:", err);
      if (!silent) {
        setUserHoldList([]);
        setUserCurrentHold(0);
      }
    } finally {
      if (!silent) setLoadingUserHolds(false);
    }
  }, [isAdmin]);

  // 6. Fetch Admin Holds for a User (GET /load_amount_hold_list?user_id=...)
  const fetchAdminHolds = useCallback(async (targetUid = "", silent = false) => {
    if (!isAdmin) return;
    if (!silent) setLoadingAdminHolds(true);
    try {
      const uidQuery = targetUid ? `?user_id=${targetUid}` : holdSearchUserId ? `?user_id=${holdSearchUserId}` : "";
      const data = await fetchWrapper(`${BASE_URL}/load_amount_hold_list${uidQuery}`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        setAdminHoldList(Array.isArray(data.data) ? data.data : []);
        setAdminCurrentHold(data.current_hold || 0);
      } else {
        if (!silent) {
          setAdminHoldList([]);
          setAdminCurrentHold(0);
        }
      }
      setLastSyncTime(new Date().toLocaleTimeString());
    } catch (err) {
      console.warn("fetchAdminHolds error:", err);
      if (!silent) {
        setAdminHoldList([]);
        setAdminCurrentHold(0);
      }
    } finally {
      if (!silent) setLoadingAdminHolds(false);
    }
  }, [holdSearchUserId, isAdmin]);

  // 7. Fetch Provider Accounts (GET /self_account_list)
  const fetchSelfAccountList = useCallback(async (silent = false) => {
    if (isAdmin) return;
    if (!silent) setLoadingProviderAccounts(true);
    try {
      const data = await fetchWrapper(`${BASE_URL}/self_account_list`, {
        method: "GET",
      });
      if (data && data.status !== false) {
        const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        setProviderAccounts(list);
      } else {
        if (!silent) setProviderAccounts([]);
      }
    } catch (err) {
      console.warn("fetchSelfAccountList error:", err);
      if (!silent) setProviderAccounts([]);
    } finally {
      if (!silent) setLoadingProviderAccounts(false);
    }
  }, [isAdmin]);

  // ─────────────────────────────────────────────────────────────
  // Hard Refresh Handler (Force network fetch across all active modules)
  // ─────────────────────────────────────────────────────────────
  const handleHardRefresh = useCallback(async (showToastNotice = true) => {
    setIsHardRefreshing(true);
    try {
      if (isAdmin) {
        await Promise.all([
          fetchAdminLoadList(false),
          fetchAdminBankList(false),
          fetchAdminHolds(holdSearchUserId, false),
        ]);
      } else {
        await Promise.all([
          fetchBankList(false),
          fetchLoadRequests(false),
          fetchUserHolds(false),
          fetchSelfAccountList(false),
        ]);
      }
      setLastSyncTime(new Date().toLocaleTimeString());
      if (showToastNotice) {
        ShowToast("Data refreshed from server!", "success");
      }
    } catch (err) {
      console.error("Hard refresh error:", err);
      if (showToastNotice) {
        ShowToast("Refresh error. Please try again.", "error");
      }
    } finally {
      setIsHardRefreshing(false);
    }
  }, [isAdmin, holdSearchUserId, fetchAdminLoadList, fetchAdminBankList, fetchAdminHolds, fetchBankList, fetchLoadRequests, fetchUserHolds, fetchSelfAccountList]);

  // ─────────────────────────────────────────────────────────────
  // Effect Hooks for Lifecycle & Background Sync
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    handleHardRefresh(false);
  }, [handleHardRefresh]);

  // Trigger hard fetch on tab change
  useEffect(() => {
    if (isAdmin) {
      if (activeTab === "admin_loads") fetchAdminLoadList(false);
      if (activeTab === "admin_banks") fetchAdminBankList(false);
      if (activeTab === "admin_holds") fetchAdminHolds(holdSearchUserId, false);
    } else {
      if (activeTab === "my_banks") fetchBankList(false);
      if (activeTab === "load_fund") {
        fetchSelfAccountList(false);
      }
      if (activeTab === "load_requests") fetchLoadRequests(false);
      if (activeTab === "provider_banks") fetchSelfAccountList(false);
      if (activeTab === "my_holds") fetchUserHolds(false);
    }
  }, [activeTab, isAdmin, holdSearchUserId, fetchAdminLoadList, fetchAdminBankList, fetchAdminHolds, fetchBankList, fetchLoadRequests, fetchUserHolds, fetchSelfAccountList]);

  // Real-time background sync polling every 5s + window focus
  useEffect(() => {
    const syncAll = () => {
      if (isAdmin) {
        fetchAdminLoadList(true);
        fetchAdminBankList(true);
        fetchAdminHolds(holdSearchUserId, true);
      } else {
        fetchBankList(true);
        fetchLoadRequests(true);
        fetchUserHolds(true);
        fetchSelfAccountList(true);
      }
    };

    const intervalId = setInterval(syncAll, 5000);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncAll();
      }
    };

    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchBankList, fetchAdminBankList, fetchAdminLoadList, fetchLoadRequests, fetchAdminHolds, fetchUserHolds, fetchSelfAccountList, holdSearchUserId, isAdmin]);

  const handleExportBankData = (data, fileName, format = "xlsx") => {
    if (!data || !data.length) {
      ShowToast("No data to export", "warning");
      return;
    }

    let exportRows = data;
    if (fileName.includes("deposit_requests")) {
      exportRows = data.map((r) => ({
        "Request ID": r.id || "",
        "User / Partner": r.user_name ? `${r.user_name} (${r.user_phone || ""})` : "",
        "User ID": r.user_id || "",
        "Deposit Amount (₹)": parseFloat(r.amount) || 0,
        "Target Wallet": r.wallet_type || "",
        "Bank UTR": r.utr_number || "",
        "Deposited From (User Bank)": r.bank_name || "",
        "User Account Number": r.account_number || "",
        "Deposited To (Provider Bank)": r.provider_bank_name || "",
        "Provider A/C Number": r.provider_account_number || "",
        "Provider IFSC": r.provider_ifsc || "",
        "Provider A/C Holder": r.provider_account_holder || "",
        "Status": r.status || "",
        "User Remark": r.remark || "",
        "Rejection Reason": r.rejection_reason || "",
        "Created At": r.created_at || "",
        "Reviewed At": r.reviewed_at || "",
      }));
    } else if (fileName.includes("provider_accounts")) {
      exportRows = data.map((r) => ({
        "ID": r.id || "",
        "Bank Name": r.bank_name || "",
        "Account Number": r.account_number || "",
        "IFSC Code": r.ifsc || "",
        "Account Holder": r.account_holder || "",
        "Branch": r.branch || "",
        "UPI ID": r.upi_id || "",
        "Status": r.status || "Active",
      }));
    } else if (fileName.includes("whitelisted_banks")) {
      exportRows = data.map((r) => ({
        "ID": r.id || "",
        "User Name": r.user_name || r.account_holder || "",
        "User Phone": r.user_phone || "",
        "Bank Name": r.bank_name || "",
        "Account Number": r.account_number || "",
        "IFSC Code": r.ifsc || "",
        "Account Holder": r.account_holder || "",
        "Status": r.status || "",
        "Admin Remark": r.remark || "",
        "Created At": r.created_at || "",
        "Reviewed At": r.reviewed_at || "",
      }));
    }

    exportDataToFile(exportRows, fileName, format);
  };

  // ─────────────────────────────────────────────────────────────
  // 1. Handle Submit Bank Whitelist (POST /bank_whitelist_submit)
  // ─────────────────────────────────────────────────────────────
  const handleChequeFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      ShowToast("Cheque image must be less than 5MB", "error");
      return;
    }
    setChequeFile(file);
    const previewUrl = URL.createObjectURL(file);
    setChequePreview(previewUrl);
  };

  const handleClearCheque = () => {
    setChequeFile(null);
    setChequePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmitBank = async (e) => {
    e.preventDefault();
    if (!bankName.trim()) {
      ShowToast("Please enter or select a Bank Name", "error");
      return;
    }
    if (!accountNumber.trim()) {
      ShowToast("Please enter Account Number", "error");
      return;
    }
    if (accountNumber.trim() !== confirmAccount.trim()) {
      ShowToast("Account numbers do not match! Please verify.", "error");
      return;
    }
    if (!ifsc.trim() || ifsc.trim().length < 8) {
      ShowToast("Please enter a valid IFSC code", "error");
      return;
    }
    if (!accountHolder.trim()) {
      ShowToast("Please enter Account Holder Name", "error");
      return;
    }
    if (!chequeFile) {
      ShowToast("Please upload a Cancelled Cheque image", "error");
      return;
    }

    setSubmittingBank(true);
    try {
      const fd = new FormData();
      fd.append("bank_name", bankName.trim());
      fd.append("account_number", accountNumber.trim());
      fd.append("ifsc", ifsc.trim().toUpperCase());
      fd.append("account_holder", accountHolder.trim());
      fd.append("cancelled_cheque", chequeFile);

      const data = await fetchWrapper(`${BASE_URL}/bank_whitelist_submit`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message || "Bank account submitted successfully for admin approval.",
          "success"
        );
        // Reset form
        setBankName("");
        setAccountNumber("");
        setConfirmAccount("");
        setIfsc("");
        setAccountHolder("");
        handleClearCheque();
        // Hard refresh lists
        await handleHardRefresh(false);
        setActiveTab("my_banks");
      } else {
        ShowToast(data?.message || data?.data || "Failed to submit bank account", "error");
      }
    } catch (err) {
      console.error("bank_whitelist_submit error:", err);
      ShowToast(err.message || "Error submitting bank account. Please try again.", "error");
    } finally {
      setSubmittingBank(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 2. Handle Admin Review Bank Whitelist (POST /bank_whitelist_admin_review)
  // ─────────────────────────────────────────────────────────────
  const openReviewBankModal = (item, decision) => {
    setSelectedBankItem(item);
    setReviewDecision(decision);
    setReviewRemark(
      decision === "approve"
        ? "Verified against cancelled cheque"
        : "Name mismatch on cheque"
    );
    setReviewBankModal(true);
  };

  // Direct 1-click Quick Approve
  const handleQuickApproveBank = async (item) => {
    if (!item || !item.id) return;
    try {
      // Optimistic instant state update
      setAdminBankList((prev) =>
        prev.map((b) => (b.id === item.id ? { ...b, status: "approved" } : b))
      );

      const fd = new FormData();
      fd.append("id", item.id);
      fd.append("decision", "approve");
      fd.append("remark", "Verified against cancelled cheque");

      const data = await fetchWrapper(`${BASE_URL}/bank_whitelist_admin_review`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message || `Bank account for ${item.account_holder || "user"} approved successfully!`,
          "success"
        );
        // Hard refresh
        await handleHardRefresh(false);
      } else {
        ShowToast(data?.message || "Failed to approve bank account", "error");
        await handleHardRefresh(false);
      }
    } catch (err) {
      console.error("Quick approve error:", err);
      ShowToast(err.message || "Server error during approval", "error");
      await handleHardRefresh(false);
    }
  };

  const handleReviewBankSubmit = async () => {
    if (!selectedBankItem) return;
    setReviewingBank(true);
    try {
      const decision = reviewDecision;
      const remark = reviewRemark.trim();

      // Optimistic instant state update
      setAdminBankList((prev) =>
        prev.map((b) =>
          b.id === selectedBankItem.id ? { ...b, status: decision, remark } : b
        )
      );

      const fd = new FormData();
      fd.append("id", selectedBankItem.id);
      fd.append("decision", decision);
      fd.append("remark", remark);

      const data = await fetchWrapper(`${BASE_URL}/bank_whitelist_admin_review`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message || `Bank account ${decision === "approve" ? "approved" : "rejected"} successfully!`,
          "success"
        );
        setReviewBankModal(false);
        setSelectedBankItem(null);
        // Hard refresh
        await handleHardRefresh(false);
      } else {
        ShowToast(data?.message || "Failed to process review", "error");
        await handleHardRefresh(false);
      }
    } catch (err) {
      console.error("bank_whitelist_admin_review error:", err);
      ShowToast(err.message || "Server error during bank review", "error");
    } finally {
      setReviewingBank(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 3. Handle Submit Load Amount Request (POST /load_amount_submit)
  // ─────────────────────────────────────────────────────────────
  const handleDepositSlipSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      ShowToast("Deposit slip image must be less than 5MB", "error");
      return;
    }
    setDepositSlipFile(file);
    const previewUrl = URL.createObjectURL(file);
    setDepositSlipPreview(previewUrl);
  };

  const handleClearSlip = () => {
    setDepositSlipFile(null);
    setDepositSlipPreview("");
    if (slipInputRef.current) slipInputRef.current.value = "";
  };

  const handleSubmitLoadAmount = async (e) => {
    e.preventDefault();
    if (!providerAccount) {
      ShowToast("Please select a Provider Bank Account", "error");
      return;
    }
    if (!loadBankId) {
      ShowToast("Please select an approved Whitelisted Bank Account", "error");
      return;
    }
    if (!loadAmount || parseFloat(loadAmount) <= 0) {
      ShowToast("Please enter a valid deposit amount", "error");
      return;
    }
    if (!loadUtr.trim()) {
      ShowToast("Please enter the Bank UTR / Reference Number", "error");
      return;
    }
    if (!depositSlipFile) {
      ShowToast("Please upload the Deposit Slip / Payment Screenshot", "error");
      return;
    }

    setSubmittingLoad(true);
    try {
      const fd = new FormData();
      fd.append("provider_account", providerAccount);
      fd.append("provider_account_id", providerAccount);
      fd.append("bank_account", loadBankId);
      fd.append("bank_account_id", loadBankId);
      fd.append("bank_id", loadBankId);
      fd.append("wallet_type", loadWalletType);
      fd.append("amount", loadAmount);
      fd.append("utr_number", loadUtr.trim());
      fd.append("remark", loadRemark.trim() || `NEFT deposit for ${loadWalletType} top-up`);
      fd.append("deposit_slip", depositSlipFile);

      const data = await fetchWrapper(`${BASE_URL}/load_amount_submit`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message || "Load amount request submitted for admin approval.",
          "success"
        );
        // Reset load form
        setProviderAccount("");
        setLoadAmount("");
        setLoadUtr("");
        setLoadRemark("");
        handleClearSlip();
        // Hard refresh
        await handleHardRefresh(false);
      } else {
        ShowToast(data?.message || data?.data || "Failed to submit load amount request", "error");
      }
    } catch (err) {
      console.error("load_amount_submit error:", err);
      ShowToast(err.message || "Error submitting load request. Please try again.", "error");
    } finally {
      setSubmittingLoad(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 4. Handle Admin Review Load Request (POST /load_amount_admin_review)
  // ─────────────────────────────────────────────────────────────
  const openReviewLoadModal = (item, decision) => {
    setSelectedLoadItem(item);
    setReviewLoadDecision(decision);
    setReviewLoadRemark(
      decision === "approve"
        ? "UTR verified in bank statement"
        : "UTR not found in bank statement"
    );
    setReviewLoadModal(true);
  };

  // Direct 1-click Quick Approve & Credit Load Request
  const handleQuickApproveLoad = async (item) => {
    if (!item || !item.id) return;
    try {
      // Optimistic update
      setAdminLoadList((prev) =>
        prev.map((l) => (l.id === item.id ? { ...l, status: "approved" } : l))
      );

      const fd = new FormData();
      fd.append("id", item.id);
      fd.append("decision", "approve");
      fd.append("remark", "UTR verified in bank statement");

      const data = await fetchWrapper(`${BASE_URL}/load_amount_admin_review`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message || `₹${item.amount} approved and credited to ${item.wallet_type}!`,
          "success"
        );
        // Hard refresh
        await handleHardRefresh(false);
      } else {
        ShowToast(data?.message || "Failed to approve deposit request", "error");
        await handleHardRefresh(false);
      }
    } catch (err) {
      console.error("Quick approve load error:", err);
      ShowToast(err.message || "Server error during load approval", "error");
      await handleHardRefresh(false);
    }
  };

  const handleReviewLoadSubmit = async () => {
    if (!selectedLoadItem) return;
    setReviewingLoad(true);
    try {
      const decision = reviewLoadDecision;
      const remark = reviewLoadRemark.trim();

      // Optimistic update
      setAdminLoadList((prev) =>
        prev.map((l) =>
          l.id === selectedLoadItem.id ? { ...l, status: decision, remark } : l
        )
      );

      const fd = new FormData();
      fd.append("id", selectedLoadItem.id);
      fd.append("decision", decision);
      fd.append("remark", remark);

      const data = await fetchWrapper(`${BASE_URL}/load_amount_admin_review`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message ||
          (decision === "approve"
            ? `₹${selectedLoadItem.amount} approved and credited to ${selectedLoadItem.wallet_type}!`
            : "Load request rejected."),
          "success"
        );
        setReviewLoadModal(false);
        setSelectedLoadItem(null);
        // Hard refresh
        await handleHardRefresh(false);
      } else {
        ShowToast(data?.message || "Failed to process load review", "error");
        await handleHardRefresh(false);
      }
    } catch (err) {
      console.error("load_amount_admin_review error:", err);
      ShowToast(err.message || "Server error during load request review", "error");
    } finally {
      setReviewingLoad(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // 5. Handle Admin Hold / Release Wallet Amount (POST /load_amount_hold, POST /load_amount_unhold)
  // ─────────────────────────────────────────────────────────────
  const openHoldModal = (type = "hold", userObj = null) => {
    setHoldActionModal({
      open: true,
      type,
      userId: userObj?.user_id || userObj?.id || "",
      userName: userObj?.user_name || "",
      walletType: userObj?.wallet_type || "main_wallet",
      amount: "",
      reason:
        type === "hold"
          ? "UTR under bank verification"
          : "Bank confirmed deposit is genuine",
    });
  };

  const handleHoldActionSubmit = async (e) => {
    e.preventDefault();
    const { type, userId: targetUid, walletType, amount, reason } = holdActionModal;

    if (!targetUid) {
      ShowToast("Please enter or select a valid User ID", "error");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      ShowToast("Please enter a valid amount", "error");
      return;
    }
    if (!reason.trim()) {
      ShowToast(`Please provide a reason for ${type === "hold" ? "holding" : "releasing"} funds`, "error");
      return;
    }

    setSubmittingHoldAction(true);
    try {
      const endpoint = type === "hold" ? "/load_amount_hold" : "/load_amount_unhold";
      const fd = new FormData();
      fd.append("user_id", String(targetUid).trim());
      fd.append("wallet_type", walletType);
      fd.append("amount", amount);
      fd.append("reason", reason.trim());

      const data = await fetchWrapper(`${BASE_URL}${endpoint}`, {
        method: "POST",
        body: fd,
      });

      if (data && data.status !== false) {
        ShowToast(
          data.message ||
          (type === "hold"
            ? `₹${amount} held from ${walletType} for UID #${targetUid}.`
            : `₹${amount} released to ${walletType} for UID #${targetUid}.`),
          "success"
        );
        setHoldActionModal((prev) => ({ ...prev, open: false }));
        // Hard refresh
        await handleHardRefresh(false);
      } else {
        ShowToast(data?.message || data?.data || `Failed to ${type} wallet balance`, "error");
      }
    } catch (err) {
      console.error("Hold action error:", err);
      ShowToast(err.message || `Server error while processing wallet ${type}`, "error");
    } finally {
      setSubmittingHoldAction(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Helper copy to clipboard
  // ─────────────────────────────────────────────────────────────
  const handleCopy = (text, label = "Details") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    ShowToast(`${label} copied to clipboard!`, "success");
  };

  // ─────────────────────────────────────────────────────────────
  // Filtered lists
  // ─────────────────────────────────────────────────────────────
  const approvedBanks = bankList.filter((b) => (b.status || "").toLowerCase() === "approved");
  const pendingBanksCount = bankList.filter((b) => (b.status || "").toLowerCase() === "pending").length;
  const approvedBanksCount = approvedBanks.length;
  const rejectedBanksCount = bankList.filter((b) => (b.status || "").toLowerCase() === "rejected").length;

  const adminPendingCount = adminBankList.filter((b) => (b.status || "").toLowerCase() === "pending").length;
  const adminPendingLoadCount = adminLoadList.filter((l) => (l.status || "").toLowerCase() === "pending").length;

  const filteredBanks = bankList.filter((b) => {
    const matchSearch =
      !bankSearch.trim() ||
      (b.bank_name || "").toLowerCase().includes(bankSearch.toLowerCase()) ||
      (b.account_number || "").includes(bankSearch) ||
      (b.account_holder || "").toLowerCase().includes(bankSearch.toLowerCase()) ||
      (b.ifsc || "").toLowerCase().includes(bankSearch.toLowerCase());

    const matchStatus =
      bankStatusFilter === "all" ||
      (b.status || "").toLowerCase() === bankStatusFilter.toLowerCase();

    return matchSearch && matchStatus;
  });

  const filteredAdminBanks = adminBankList.filter((b) => {
    return (
      !adminBankSearch.trim() ||
      (b.user_name || "").toLowerCase().includes(adminBankSearch.toLowerCase()) ||
      (b.user_phone || "").includes(adminBankSearch) ||
      (b.bank_name || "").toLowerCase().includes(adminBankSearch.toLowerCase()) ||
      (b.account_number || "").includes(adminBankSearch) ||
      (b.account_holder || "").toLowerCase().includes(adminBankSearch.toLowerCase()) ||
      (b.ifsc || "").toLowerCase().includes(adminBankSearch.toLowerCase()) ||
      String(b.user_id || "").includes(adminBankSearch)
    );
  });

  const filteredAdminLoads = adminLoadList.filter((l) => {
    return (
      !adminLoadSearch.trim() ||
      (l.user_name || "").toLowerCase().includes(adminLoadSearch.toLowerCase()) ||
      (l.user_phone || "").includes(adminLoadSearch) ||
      (l.utr_number || "").toLowerCase().includes(adminLoadSearch.toLowerCase()) ||
      (l.bank_name || "").toLowerCase().includes(adminLoadSearch.toLowerCase()) ||
      String(l.amount || "").includes(adminLoadSearch) ||
      String(l.user_id || "").includes(adminLoadSearch)
    );
  });

  const filteredProviderAccounts = providerAccounts.filter((acc) => {
    if (!providerSearch.trim()) return true;
    const q = providerSearch.toLowerCase();
    return (
      (acc.bank_name || "").toLowerCase().includes(q) ||
      (acc.account_number || "").includes(q) ||
      (acc.account_holder || "").toLowerCase().includes(q) ||
      (acc.ifsc || "").toLowerCase().includes(q) ||
      (acc.branch || "").toLowerCase().includes(q) ||
      (acc.upi_id || "").toLowerCase().includes(q)
    );
  });

  const filteredUserLoads = loadRequests.filter((l) => {
    const q = userLoadSearch.trim().toLowerCase();
    const matchSearch =
      !q ||
      (l.bank_name || "").toLowerCase().includes(q) ||
      (l.account_number || "").includes(q) ||
      (l.provider_bank_name || "").toLowerCase().includes(q) ||
      (l.provider_account_number || "").includes(q) ||
      (l.provider_account_holder || "").toLowerCase().includes(q) ||
      (l.utr_number || "").toLowerCase().includes(q) ||
      (l.remark || "").toLowerCase().includes(q) ||
      (l.wallet_type || "").toLowerCase().includes(q) ||
      String(l.amount || "").includes(q) ||
      String(l.id || "").includes(q);

    const matchStatus =
      userLoadStatusFilter === "all" ||
      (l.status || "").toLowerCase() === userLoadStatusFilter.toLowerCase();

    return matchSearch && matchStatus;
  });

  return (
    <div className="rb-wrapper">
      {/* ── Top Hero Bar ── */}
      <div className="rb-header-banner">
        <div className="rb-header-left">
          <div className="rb-hero-icon-circle">
            <span>{isAdmin ? "🛡️" : "🏛️"}</span>
          </div>
          <div>
            <div className="rb-breadcrumb">
              <span>Home</span> › <span>Banking &amp; Settlement</span> ›{" "}
              <span className="rb-active-crumb">
                {isAdmin ? "Deposit Requests & Whitelisting" : "Bank Accounts & Load Funds"}
              </span>
            </div>
            <h1 className="rb-hero-title">
              {isAdmin ? "Admin Deposit Requests & Bank Whitelisting" : "Bank Accounts & Wallet Load Requests"}
            </h1>
            <p className="rb-hero-subtitle">
              {isAdmin
                ? "Review and approve user deposit requests, verify cancelled cheque proofs for bank whitelisting, and manage wallet balance holds."
                : "Submit bank accounts for admin whitelisting, upload cancelled cheques, and request wallet deposit credits."}
            </p>
          </div>
        </div>

        <div className="rb-header-actions">
          {/* Hard Refresh Button in Hero Banner */}
          <button
            type="button"
            className="rb-action-btn rb-btn-sync"
            onClick={() => handleHardRefresh(true)}
            disabled={isHardRefreshing}
            title={`Last synced at ${lastSyncTime}. Click for hard refresh from server.`}
          >
            <span className={isHardRefreshing ? "rb-spin" : ""}>🔄</span>
            <span>{isHardRefreshing ? "Refreshing..." : "Hard Refresh"}</span>
            <span className="rb-sync-time">{lastSyncTime}</span>
          </button>

          {isAdmin ? (
            <>
              <button
                type="button"
                className={`rb-action-btn ${activeTab === "admin_loads" ? "rb-btn-active" : "rb-btn-accent"}`}
                onClick={() => {
                  setAdminLoadStatusFilter("pending");
                  setActiveTab("admin_loads");
                  fetchAdminLoadList(false);
                }}
              >
                <span>💼</span> Deposit Requests
                {adminPendingLoadCount > 0 && <span className="rb-pill-count">{adminPendingLoadCount}</span>}
              </button>

              <button
                type="button"
                className={`rb-action-btn ${activeTab === "admin_banks" ? "rb-btn-active" : "rb-btn-primary"}`}
                onClick={() => {
                  setAdminBankStatusFilter("pending");
                  setActiveTab("admin_banks");
                  fetchAdminBankList(false);
                }}
              >
                <span>🛡️</span> Whitelist Requests
                {adminPendingCount > 0 && <span className="rb-pill-count">{adminPendingCount}</span>}
              </button>

              <button
                type="button"
                className={`rb-action-btn ${activeTab === "admin_holds" ? "rb-btn-active" : "rb-btn-outline"}`}
                onClick={() => {
                  setActiveTab("admin_holds");
                  fetchAdminHolds(holdSearchUserId, false);
                }}
              >
                <span>🔒</span> Wallet Holds
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="rb-action-btn rb-btn-primary"
                onClick={() => setSubmitBankModalOpen(true)}
              >
                <span>➕</span> Submit Bank Account
              </button>
              {approvedBanksCount > 0 && (
                <button
                  type="button"
                  className={`rb-action-btn ${activeTab === "load_fund" ? "rb-btn-active" : "rb-btn-outline"}`}
                  onClick={() => {
                    if (!loadBankId && approvedBanks[0]) setLoadBankId(approvedBanks[0].id);
                    setActiveTab("load_fund");
                    fetchSelfAccountList(false);
                  }}
                >
                  <span>⚡</span> Deposit Funds
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Admin Pending Whitelist Alert Banner (Admin Only) ── */}
      {isAdmin && (adminPendingLoadCount > 0 || adminPendingCount > 0) && (
        <div className="rb-admin-alert-banner">
          <div className="rb-alert-content">
            <span className="rb-alert-bell">🔔</span>
            <div>
              <strong>Action Required:</strong> You have{" "}
              {adminPendingLoadCount > 0 && (
                <span className="rb-alert-highlight">
                  {adminPendingLoadCount} Deposit Request(s){" "}
                </span>
              )}
              {adminPendingLoadCount > 0 && adminPendingCount > 0 && "and "}
              {adminPendingCount > 0 && (
                <span className="rb-alert-highlight">
                  {adminPendingCount} Bank Whitelist Request(s){" "}
                </span>
              )}
              awaiting admin verification &amp; processing.
            </div>
          </div>
          <button
            type="button"
            className="rb-alert-btn"
            onClick={() => {
              if (adminPendingLoadCount > 0) {
                setAdminLoadStatusFilter("pending");
                setActiveTab("admin_loads");
                fetchAdminLoadList(false);
              } else {
                setAdminBankStatusFilter("pending");
                setActiveTab("admin_banks");
                fetchAdminBankList(false);
              }
            }}
          >
            Review Pending Queue →
          </button>
        </div>
      )}

      {/* ── Stats Metric Cards ── */}
      <div className="rb-metrics-grid">
        {isAdmin ? (
          <>
            <div
              className="rb-metric-card"
              onClick={() => {
                setAdminLoadStatusFilter("pending");
                setActiveTab("admin_loads");
                fetchAdminLoadList(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-amber">
                <span>💼</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Pending Deposit Requests</span>
                <div className="rb-metric-value text-warning">{adminPendingLoadCount}</div>
                <span className="rb-metric-foot">Awaiting UTR credit check</span>
              </div>
            </div>

            <div
              className="rb-metric-card"
              onClick={() => {
                setAdminBankStatusFilter("pending");
                setActiveTab("admin_banks");
                fetchAdminBankList(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-blue">
                <span>🛡️</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Pending Bank Whitelists</span>
                <div className="rb-metric-value text-primary">{adminPendingCount}</div>
                <span className="rb-metric-foot">Awaiting cheque verification</span>
              </div>
            </div>

            <div
              className="rb-metric-card"
              onClick={() => {
                setAdminLoadStatusFilter("approved");
                setActiveTab("admin_loads");
                fetchAdminLoadList(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-green">
                <span>✅</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Approved Deposits</span>
                <div className="rb-metric-value text-success">
                  {adminLoadList.filter((l) => (l.status || "").toLowerCase() === "approved").length}
                </div>
                <span className="rb-metric-foot">Processed &amp; credited</span>
              </div>
            </div>

            <div
              className="rb-metric-card"
              onClick={() => {
                setActiveTab("admin_holds");
                fetchAdminHolds(holdSearchUserId, false);
              }}
            >
              <div className="rb-metric-icon rb-icon-red">
                <span>🔒</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Wallet Holds</span>
                <div className="rb-metric-value text-danger">
                  {adminHoldList.filter((h) => (h.action || "").toLowerCase() === "hold").length}
                </div>
                <span className="rb-metric-foot">Risk &amp; dispute holds</span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className="rb-metric-card"
              onClick={() => {
                setBankStatusFilter("approved");
                setActiveTab("my_banks");
                fetchBankList(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-green">
                <span>✅</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Approved Bank Accounts</span>
                <div className="rb-metric-value text-success">{approvedBanksCount}</div>
                <span className="rb-metric-foot">Active for fund deposit</span>
              </div>
            </div>

            <div
              className="rb-metric-card"
              onClick={() => {
                setBankStatusFilter("pending");
                setActiveTab("my_banks");
                fetchBankList(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-amber">
                <span>⏳</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Pending Verification</span>
                <div className="rb-metric-value text-warning">{pendingBanksCount}</div>
                <span className="rb-metric-foot">Under admin review</span>
              </div>
            </div>

            <div
              className="rb-metric-card"
              onClick={() => {
                setActiveTab("load_requests");
                fetchLoadRequests(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-blue">
                <span>⚡</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">My Deposit Requests</span>
                <div className="rb-metric-value">{loadRequests.length}</div>
                <span className="rb-metric-foot">History of load requests</span>
              </div>
            </div>

            <div
              className="rb-metric-card"
              onClick={() => {
                setActiveTab("my_holds");
                fetchUserHolds(false);
              }}
            >
              <div className="rb-metric-icon rb-icon-red">
                <span>🔒</span>
              </div>
              <div className="rb-metric-info">
                <span className="rb-metric-label">Current Held Balance</span>
                <div className="rb-metric-value text-danger">₹{userCurrentHold.toLocaleString("en-IN")}</div>
                <span className="rb-metric-foot">Security / verification holds</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Main Navigation Tabs ── */}
      <div className="rb-nav-tabs">
        {isAdmin ? (
          <>
            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "admin_loads" ? "rb-tab-selected rb-admin-tab" : "rb-admin-tab"}`}
              onClick={() => {
                setActiveTab("admin_loads");
                fetchAdminLoadList(false);
              }}
            >
              <span>💼</span> Admin Deposit Requests
              {adminPendingLoadCount > 0 ? (
                <span className="rb-tab-count rb-badge-pending-count">{adminPendingLoadCount} Pending</span>
              ) : (
                <span className="rb-tab-count">{adminLoadList.length}</span>
              )}
            </button>

            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "admin_banks" ? "rb-tab-selected rb-admin-tab" : "rb-admin-tab"}`}
              onClick={() => {
                setActiveTab("admin_banks");
                fetchAdminBankList(false);
              }}
            >
              <span>🛡️</span> Bank Whitelist Requests
              {adminPendingCount > 0 ? (
                <span className="rb-tab-count rb-badge-pending-count">{adminPendingCount} Pending</span>
              ) : (
                <span className="rb-tab-count">{adminBankList.length}</span>
              )}
            </button>

            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "admin_holds" ? "rb-tab-selected rb-admin-tab" : "rb-admin-tab"}`}
              onClick={() => {
                setActiveTab("admin_holds");
                fetchAdminHolds(holdSearchUserId, false);
              }}
            >
              <span>🔒</span> Wallet Hold &amp; Release Management
              {adminHoldList.length > 0 && <span className="rb-tab-count">{adminHoldList.length}</span>}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "my_banks" ? "rb-tab-selected" : ""}`}
              onClick={() => {
                setActiveTab("my_banks");
                fetchBankList(false);
              }}
            >
              <span>🏛️</span> My Whitelisted Banks
              {bankList.length > 0 && <span className="rb-tab-count">{bankList.length}</span>}
            </button>

            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "load_fund" ? "rb-tab-selected" : ""}`}
              onClick={() => {
                setActiveTab("load_fund");
                fetchSelfAccountList(false);
              }}
            >
              <span>⚡</span> Load Amount / Deposits
            </button>

            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "load_requests" ? "rb-tab-selected" : ""}`}
              onClick={() => {
                setActiveTab("load_requests");
                fetchLoadRequests(false);
              }}
            >
              <span>📜</span> Load Requests
              {loadRequests.length > 0 && <span className="rb-tab-count">{loadRequests.length}</span>}
            </button>

            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "provider_banks" ? "rb-tab-selected" : ""}`}
              onClick={() => {
                setActiveTab("provider_banks");
                fetchSelfAccountList(false);
              }}
            >
              <span>🏢</span> Provider List
              {providerAccounts.length > 0 && <span className="rb-tab-count">{providerAccounts.length}</span>}
            </button>

            <button
              type="button"
              className={`rb-nav-tab ${activeTab === "my_holds" ? "rb-tab-selected" : ""}`}
              onClick={() => {
                setActiveTab("my_holds");
                fetchUserHolds(false);
              }}
            >
              <span>🔒</span> My Wallet Holds
              {userHoldList.length > 0 && <span className="rb-tab-count">{userHoldList.length}</span>}
            </button>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          ADMIN TAB 1: ADMIN DEPOSIT REQUESTS (LOAD AMOUNT APPROVALS)
      ══════════════════════════════════════════════════════════════ */}
      {isAdmin && activeTab === "admin_loads" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>💼</span> Admin Deposit Requests &amp; Wallet Approvals
              </h2>
              <p className="rb-card-sub">
                Verify partner UTR numbers against bank credit statements, inspect deposit slips, and instantly credit recipient wallets.
              </p>
            </div>

            <div className="rb-card-filters">
              <div className="rb-search-wrap">
                <input
                  type="text"
                  className="rb-search-inp"
                  placeholder="Search user, mobile, UID, UTR, amount..."
                  value={adminLoadSearch}
                  onChange={(e) => setAdminLoadSearch(e.target.value)}
                />
                <span className="rb-search-icon">🔍</span>
              </div>

              <select
                className="rb-select-filter"
                value={adminLoadStatusFilter}
                onChange={(e) => {
                  setAdminLoadStatusFilter(e.target.value);
                  fetchAdminLoadList(false);
                }}
              >
                <option value="pending">Pending Only</option>
                <option value="approved">Approved Only</option>
                <option value="rejected">Rejected Only</option>
                <option value="all">All Deposit Requests</option>
              </select>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredAdminLoads, "admin_deposit_requests", "xlsx")}
                  disabled={!filteredAdminLoads.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredAdminLoads, "admin_deposit_requests", "csv")}
                  disabled={!filteredAdminLoads.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchAdminLoadList(false)}
                disabled={loadingAdminLoads || isHardRefreshing}
                title="Hard refresh list from server"
              >
                <span className={loadingAdminLoads || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          {loadingAdminLoads ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading deposit requests queue...</h3>
            </div>
          ) : filteredAdminLoads.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">🎉</span>
              <h3>No Deposit Requests in Queue</h3>
              <p>There are no {adminLoadStatusFilter} load amount requests matching your current filter criteria.</p>
            </div>
          ) : (
            <div className="table-responsive rb-table-wrap">
              <table className="table table-hover rb-admin-table align-middle">
                <thead>
                  <tr>
                    <th>Req ID</th>
                    <th>Partner / User</th>
                    <th>Deposit Amount &amp; Wallet</th>
                    <th>Deposited From &amp; UTR</th>
                    <th>Deposited To (Company A/C)</th>
                    <th>Payment Slip</th>
                    <th>Status &amp; Notes</th>
                    <th>Dates</th>
                    <th className="text-end">Admin Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdminLoads.map((item) => {
                    const status = (item.status || "pending").toLowerCase();
                    const isPending = status === "pending";
                    const slipImgUrl = item.slip_url
                      ? item.slip_url.startsWith("http")
                        ? item.slip_url
                        : `${BASE_URL}${item.slip_url}`
                      : "";

                    return (
                      <tr key={item.id} className={isPending ? "rb-tr-pending-highlight" : ""}>
                        <td>
                          <span className="rb-badge-id">#{item.id}</span>
                        </td>
                        <td>
                          <div className="rb-user-cell">
                            <strong>{item.user_name || "Partner User"}</strong>
                            <span className="text-muted fs-12">📱 {item.user_phone || "N/A"}</span>
                            <span className="rb-uid-tag">UID: {item.user_id}</span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong className="rb-amt-highlight">
                              ₹{(parseFloat(item.amount) || 0).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </strong>
                            <div className="rb-wallet-tag">
                              {item.wallet_type === "recharge_wallet" ? "⚡ Recharge Wallet" : "💼 Main Wallet"}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div className="rb-info-val-wrap">
                              <span className="rb-info-mono">UTR: {item.utr_number}</span>
                              <button
                                type="button"
                                className="rb-btn-copy"
                                onClick={() => handleCopy(item.utr_number, "UTR Number")}
                                title="Copy UTR"
                              >
                                📋
                              </button>
                            </div>
                            <div className="text-muted fs-12 mt-1">
                              👤 {item.bank_name || "User Bank"} {item.account_number ? `(${item.account_number})` : ""}
                            </div>
                            {item.ifsc && <span className="rb-ifsc-badge mt-1">{item.ifsc}</span>}
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong className="text-dark fs-13 d-block">
                              🏛️ {item.provider_bank_name || "Company Account"}
                            </strong>
                            {item.provider_account_number && (
                              <div className="rb-info-val-wrap mt-1">
                                <span className="rb-info-mono">A/C: {item.provider_account_number}</span>
                                <button
                                  type="button"
                                  className="rb-btn-copy"
                                  onClick={() => handleCopy(item.provider_account_number, "Provider Account Number")}
                                  title="Copy Account Number"
                                >
                                  📋
                                </button>
                              </div>
                            )}
                            {item.provider_ifsc && (
                              <span className="rb-ifsc-badge mt-1 d-inline-block">IFSC: {item.provider_ifsc}</span>
                            )}
                            {item.provider_account_holder && (
                              <div className="text-muted fs-11 mt-1">
                                👤 {item.provider_account_holder}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {slipImgUrl ? (
                            <button
                              type="button"
                              className="rb-btn-cheque-thumb"
                              onClick={() =>
                                setPreviewImageModal({
                                  open: true,
                                  title: `Deposit Slip — ₹${item.amount} (${item.user_name || "Partner"})`,
                                  url: slipImgUrl,
                                  subtitle: `UTR: ${item.utr_number} | To: ${item.provider_bank_name || "Company"} | From: ${item.bank_name} (${item.account_number})`,
                                })
                              }
                            >
                              <img src={slipImgUrl} alt="Deposit Slip" className="rb-thumb-img" />
                              <span>🔍 View Slip</span>
                            </button>
                          ) : (
                            <span className="text-muted fs-12">No slip</span>
                          )}
                        </td>
                        <td>
                          <span className={`rb-status-badge rb-badge-${status}`}>
                            {status === "approved" && "● Approved & Credited"}
                            {status === "pending" && "⏳ Pending Review"}
                            {status === "rejected" && "✕ Rejected"}
                          </span>
                          {item.remark && (
                            <div className="text-muted fs-11 mt-1" style={{ maxWidth: "160px" }}>
                              <strong>Note:</strong> {item.remark}
                            </div>
                          )}
                          {item.rejection_reason && (
                            <div className="text-danger fs-11 mt-1" style={{ maxWidth: "160px" }}>
                              <strong>Reason:</strong> {item.rejection_reason}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="text-muted fs-12">
                            <div><strong>Created:</strong> {item.created_at || "Recent"}</div>
                            {item.reviewed_at && (
                              <div className="mt-1 text-primary fs-11">
                                <strong>Reviewed:</strong> {item.reviewed_at}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="text-end">
                          {isPending ? (
                            <div className="rb-btn-group-end">
                              <button
                                type="button"
                                className="rb-btn-sm-quick-approve"
                                onClick={() => handleQuickApproveLoad(item)}
                                title="1-Click Credit & Approve"
                              >
                                ✓ Credit Now
                              </button>
                              <button
                                type="button"
                                className="rb-btn-sm-approve"
                                onClick={() => openReviewLoadModal(item, "approve")}
                              >
                                Review...
                              </button>
                              <button
                                type="button"
                                className="rb-btn-sm-reject"
                                onClick={() => openReviewLoadModal(item, "reject")}
                              >
                                ✕ Reject
                              </button>
                              <button
                                type="button"
                                className="rb-btn-sm-hold"
                                onClick={() => openHoldModal("hold", item)}
                                title="Hold Wallet Balance for this User"
                              >
                                🔒 Hold
                              </button>
                            </div>
                          ) : (
                            <div className="rb-btn-group-end">
                              <span className="text-muted fs-12">Processed</span>
                              <button
                                type="button"
                                className="rb-btn-sm-hold"
                                onClick={() => openHoldModal("hold", item)}
                                title="Manage user wallet holds"
                              >
                                🔒 Hold
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          ADMIN TAB 2: BANK WHITELIST REQUESTS (REVIEW & WHITELIST)
      ══════════════════════════════════════════════════════════════ */}
      {isAdmin && activeTab === "admin_banks" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>🛡️</span> Bank Whitelist Requests &amp; Cheque Verification
              </h2>
              <p className="rb-card-sub">
                Inspect partner cancelled cheques, cross-verify account holder credentials, and approve bank accounts for fund loads.
              </p>
            </div>

            <div className="rb-card-filters">
              <div className="rb-search-wrap">
                <input
                  type="text"
                  className="rb-search-inp"
                  placeholder="Search user, mobile, UID, bank, A/C or IFSC..."
                  value={adminBankSearch}
                  onChange={(e) => setAdminBankSearch(e.target.value)}
                />
                <span className="rb-search-icon">🔍</span>
              </div>

              <select
                className="rb-select-filter"
                value={adminBankStatusFilter}
                onChange={(e) => {
                  setAdminBankStatusFilter(e.target.value);
                  fetchAdminBankList(false);
                }}
              >
                <option value="pending">Pending Whitelist Only</option>
                <option value="approved">Approved Whitelists Only</option>
                <option value="rejected">Rejected Whitelists Only</option>
                <option value="all">All Submissions</option>
              </select>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredAdminBanks, "admin_bank_whitelists", "xlsx")}
                  disabled={!filteredAdminBanks.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredAdminBanks, "admin_bank_whitelists", "csv")}
                  disabled={!filteredAdminBanks.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchAdminBankList(false)}
                disabled={loadingAdminBanks || isHardRefreshing}
                title="Hard refresh whitelist queue"
              >
                <span className={loadingAdminBanks || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          {loadingAdminBanks ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading bank whitelist review queue...</h3>
            </div>
          ) : filteredAdminBanks.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">🎉</span>
              <h3>No Bank Whitelist Requests Found</h3>
              <p>There are no {adminBankStatusFilter} bank accounts matching your current search.</p>
            </div>
          ) : (
            <div className="table-responsive rb-table-wrap">
              <table className="table table-hover rb-admin-table align-middle">
                <thead>
                  <tr>
                    <th>Req ID</th>
                    <th>Partner / User</th>
                    <th>Bank &amp; Account Number</th>
                    <th>IFSC Code</th>
                    <th>Account Holder</th>
                    <th>Cancelled Cheque</th>
                    <th>Status</th>
                    <th>Submitted Date</th>
                    <th className="text-end">Admin Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAdminBanks.map((item) => {
                    const status = (item.status || "pending").toLowerCase();
                    const isPending = status === "pending";
                    const chequeImgUrl = item.cheque_url
                      ? item.cheque_url.startsWith("http")
                        ? item.cheque_url
                        : `${BASE_URL}${item.cheque_url}`
                      : "";

                    return (
                      <tr key={item.id} className={isPending ? "rb-tr-pending-highlight" : ""}>
                        <td>
                          <span className="rb-badge-id">#{item.id}</span>
                        </td>
                        <td>
                          <div className="rb-user-cell">
                            <strong>{item.user_name || "Partner"}</strong>
                            <span className="text-muted fs-12">📱 {item.user_phone || "N/A"}</span>
                            <span className="rb-uid-tag">UID: {item.user_id}</span>
                          </div>
                        </td>
                        <td>
                          <div className="rb-bank-cell">
                            <strong className="text-primary">{item.bank_name}</strong>
                            <div className="rb-info-val-wrap">
                              <span className="rb-mono-text">{item.account_number}</span>
                              <button
                                type="button"
                                className="rb-btn-copy"
                                onClick={() => handleCopy(item.account_number, "Account Number")}
                                title="Copy"
                              >
                                📋
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="rb-ifsc-badge">{item.ifsc}</span>
                        </td>
                        <td>
                          <span className="fw-semibold text-dark">{item.account_holder}</span>
                        </td>
                        <td>
                          {chequeImgUrl ? (
                            <button
                              type="button"
                              className="rb-btn-cheque-thumb"
                              onClick={() =>
                                setPreviewImageModal({
                                  open: true,
                                  title: `Cancelled Cheque — ${item.user_name || item.account_holder} (${item.bank_name})`,
                                  url: chequeImgUrl,
                                  subtitle: `A/C: ${item.account_number} | IFSC: ${item.ifsc} | Holder: ${item.account_holder}`,
                                })
                              }
                            >
                              <img src={chequeImgUrl} alt="Cheque" className="rb-thumb-img" />
                              <span>🔍 View Cheque</span>
                            </button>
                          ) : (
                            <span className="text-muted fs-12">No image</span>
                          )}
                        </td>
                        <td>
                          <span className={`rb-status-badge rb-badge-${status}`}>
                            {status === "approved" && "● Whitelisted"}
                            {status === "pending" && "⏳ Pending Verification"}
                            {status === "rejected" && "✕ Rejected"}
                          </span>
                          {item.remark && (
                            <div className="text-muted fs-11 mt-1" style={{ maxWidth: "160px" }}>
                              <strong>Remark:</strong> {item.remark}
                            </div>
                          )}
                        </td>
                        <td className="text-muted fs-12">{item.created_at || "Recent"}</td>
                        <td className="text-end">
                          {isPending ? (
                            <div className="rb-btn-group-end">
                              <button
                                type="button"
                                className="rb-btn-sm-quick-approve"
                                onClick={() => handleQuickApproveBank(item)}
                                title="Approve with 1-click verification"
                              >
                                ✓ Approve
                              </button>
                              <button
                                type="button"
                                className="rb-btn-sm-approve"
                                onClick={() => openReviewBankModal(item, "approve")}
                              >
                                Review...
                              </button>
                              <button
                                type="button"
                                className="rb-btn-sm-reject"
                                onClick={() => openReviewBankModal(item, "reject")}
                              >
                                ✕ Reject
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="rb-btn-sm-review-again"
                              onClick={() => openReviewBankModal(item, status === "approved" ? "reject" : "approve")}
                            >
                              ⚙️ Change Status
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          ADMIN TAB 3: WALLET HOLD & RELEASE (UNHOLD) MANAGEMENT
      ══════════════════════════════════════════════════════════════ */}
      {isAdmin && activeTab === "admin_holds" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>🔒</span> Wallet Hold &amp; Release (Unhold) Management
              </h2>
              <p className="rb-card-sub">
                Place temporary balance holds during UTR verification and release funds once genuine credit is confirmed.
              </p>
            </div>

            <div className="rb-card-filters">
              <div className="rb-search-wrap">
                <input
                  type="text"
                  className="rb-search-inp"
                  placeholder="Filter by User ID (e.g. 452)..."
                  value={holdSearchUserId}
                  onChange={(e) => setHoldSearchUserId(e.target.value.replace(/\D/g, ""))}
                />
                <span className="rb-search-icon">🔍</span>
              </div>

              <button
                type="button"
                className="rb-action-btn rb-btn-danger"
                onClick={() => openHoldModal("hold", { user_id: holdSearchUserId })}
              >
                <span>🔒</span> Place Hold
              </button>

              <button
                type="button"
                className="rb-action-btn rb-btn-success"
                onClick={() => openHoldModal("unhold", { user_id: holdSearchUserId })}
              >
                <span>🔓</span> Release Hold
              </button>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(adminHoldList, "admin_wallet_holds", "xlsx")}
                  disabled={!adminHoldList.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(adminHoldList, "admin_wallet_holds", "csv")}
                  disabled={!adminHoldList.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchAdminHolds(holdSearchUserId, false)}
                disabled={loadingAdminHolds || isHardRefreshing}
                title="Hard refresh holds log"
              >
                <span className={loadingAdminHolds || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          {holdSearchUserId && (
            <div className="rb-hold-summary-banner">
              <div className="rb-hold-summary-left">
                <span className="rb-hold-badge-label">Active Hold for User #{holdSearchUserId}:</span>
                <strong className="rb-hold-badge-value">₹{(adminCurrentHold || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
              </div>
              <div className="rb-hold-summary-actions">
                <button
                  type="button"
                  className="rb-btn-sm-hold"
                  onClick={() => openHoldModal("hold", { user_id: holdSearchUserId })}
                >
                  + Add Hold
                </button>
                <button
                  type="button"
                  className="rb-btn-sm-unhold"
                  onClick={() => openHoldModal("unhold", { user_id: holdSearchUserId })}
                >
                  ✓ Release Amount
                </button>
              </div>
            </div>
          )}

          {loadingAdminHolds ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading wallet holds log...</h3>
            </div>
          ) : adminHoldList.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">🎉</span>
              <h3>No Wallet Holds Found</h3>
              <p>
                {holdSearchUserId
                  ? `No hold or unhold records found for User ID #${holdSearchUserId}.`
                  : "No wallet hold records registered in the system."}
              </p>
            </div>
          ) : (
            <div className="table-responsive rb-table-wrap">
              <table className="table table-hover rb-admin-table align-middle">
                <thead>
                  <tr>
                    <th>Hold ID</th>
                    <th>Target Wallet</th>
                    <th>Action</th>
                    <th>Amount</th>
                    <th>Reason / Justification</th>
                    <th>Performed By</th>
                    <th>Date &amp; Time</th>
                  </tr>
                </thead>
                <tbody>
                  {adminHoldList.map((h) => {
                    const isHold = (h.action || "").toLowerCase() === "hold";
                    return (
                      <tr key={h.id}>
                        <td>
                          <span className="rb-badge-id">#{h.id}</span>
                        </td>
                        <td>
                          <span className="rb-wallet-tag">
                            {h.wallet_type === "recharge_wallet" ? "⚡ Recharge Wallet" : "💼 Main Wallet"}
                          </span>
                        </td>
                        <td>
                          <span className={`rb-hold-action-badge ${isHold ? "rb-badge-hold" : "rb-badge-unhold"}`}>
                            {isHold ? "🔒 Hold Placed" : "🔓 Hold Released"}
                          </span>
                        </td>
                        <td>
                          <strong className={isHold ? "text-danger" : "text-success"}>
                            {isHold ? "- " : "+ "}₹{(parseFloat(h.amount) || 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </strong>
                        </td>
                        <td>
                          <span className="fw-medium text-dark">{h.reason || "Under verification"}</span>
                        </td>
                        <td>
                          <span className="text-muted fs-12">👤 {h.performed_by || "Admin"}</span>
                        </td>
                        <td className="text-muted fs-12">{h.created_at || "Recent"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          USER TAB 1: MY WHITELISTED BANK ACCOUNTS (USER VIEW)
      ══════════════════════════════════════════════════════════════ */}
      {!isAdmin && activeTab === "my_banks" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>📋</span> My Registered Whitelisted Bank Accounts
              </h2>
              <p className="rb-card-sub">
                Admin-approved bank accounts are required before you can load funds into your wallet.
              </p>
            </div>

            <div className="rb-card-filters">
              <div className="rb-search-wrap">
                <input
                  type="text"
                  className="rb-search-inp"
                  placeholder="Search bank name, A/C or IFSC..."
                  value={bankSearch}
                  onChange={(e) => setBankSearch(e.target.value)}
                />
                <span className="rb-search-icon">🔍</span>
              </div>

              <select
                className="rb-select-filter"
                value={bankStatusFilter}
                onChange={(e) => {
                  setBankStatusFilter(e.target.value);
                  fetchBankList(false);
                }}
              >
                <option value="all">All Statuses</option>
                <option value="approved">Approved Accounts</option>
                <option value="pending">Pending Approval</option>
                <option value="rejected">Rejected Accounts</option>
              </select>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  style={{ fontWeight: 600, padding: "6px 14px", borderRadius: "8px", background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)", border: "none" }}
                  onClick={() => setSubmitBankModalOpen(true)}
                >
                  ➕ Submit Bank Account
                </button>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredBanks, "my_whitelisted_banks", "xlsx")}
                  disabled={!filteredBanks.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredBanks, "my_whitelisted_banks", "csv")}
                  disabled={!filteredBanks.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchBankList(false)}
                disabled={loadingBanks || isHardRefreshing}
                title="Hard refresh bank accounts"
              >
                <span className={loadingBanks || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          {loadingBanks ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading whitelisted bank accounts...</h3>
            </div>
          ) : filteredBanks.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">🏛️</span>
              <h3>No Bank Accounts Found</h3>
              <p>
                {bankList.length === 0
                  ? "You have not submitted any bank account for whitelisting yet. Submit one now to start loading funds."
                  : "No bank accounts matching your current search and filter criteria."}
              </p>
              <button
                type="button"
                className="rb-action-btn rb-btn-primary mt-3"
                onClick={() => setSubmitBankModalOpen(true)}
              >
                ➕ Submit Bank Account
              </button>
            </div>
          ) : (
            <div className="rb-banks-grid">
              {filteredBanks.map((item) => {
                const status = (item.status || "pending").toLowerCase();
                const isApproved = status === "approved";
                const isPending = status === "pending";
                const isRejected = status === "rejected";

                return (
                  <div key={item.id} className={`rb-bank-card rb-border-${status}`}>
                    <div className="rb-bank-card-top">
                      <div className="rb-bank-avatar">
                        <span>🏛️</span>
                      </div>
                      <div className="rb-bank-title-group">
                        <h3 className="rb-bank-name">{item.bank_name || "Bank Account"}</h3>
                        <span className="rb-holder-name">
                          👤 {item.account_holder || "Account Holder"}
                        </span>
                      </div>
                      <span className={`rb-status-badge rb-badge-${status}`}>
                        {isApproved && "● Approved"}
                        {isPending && "⏳ Pending Approval"}
                        {isRejected && "✕ Rejected"}
                      </span>
                    </div>

                    <div className="rb-bank-card-body">
                      <div className="rb-info-row">
                        <span className="rb-info-lbl">Account Number</span>
                        <div className="rb-info-val-wrap">
                          <span className="rb-info-mono">{item.account_number}</span>
                          <button
                            type="button"
                            className="rb-btn-copy"
                            onClick={() => handleCopy(item.account_number, "Account Number")}
                            title="Copy Account Number"
                          >
                            📋
                          </button>
                        </div>
                      </div>

                      <div className="rb-info-row">
                        <span className="rb-info-lbl">IFSC Code</span>
                        <div className="rb-info-val-wrap">
                          <span className="rb-info-mono">{item.ifsc}</span>
                          <button
                            type="button"
                            className="rb-btn-copy"
                            onClick={() => handleCopy(item.ifsc, "IFSC Code")}
                            title="Copy IFSC"
                          >
                            📋
                          </button>
                        </div>
                      </div>

                      {item.remark && (
                        <div className="rb-remark-box">
                          <strong>Admin Remark:</strong> {item.remark}
                        </div>
                      )}

                      <div className="rb-meta-row">
                        <span>Submitted: {item.created_at || "Recent"}</span>
                        {item.reviewed_at && <span>Reviewed: {item.reviewed_at}</span>}
                      </div>
                    </div>

                    <div className="rb-bank-card-foot">
                      {isApproved && (
                        <button
                          type="button"
                          className="rb-btn-card-load"
                          onClick={() => {
                            setLoadBankId(item.id);
                            setActiveTab("load_fund");
                            fetchSelfAccountList(false);
                          }}
                        >
                          ⚡ Load Funds With This Bank
                        </button>
                      )}
                      {isPending && (
                        <div className="rb-notice-pending">
                          ℹ️ Admin review in progress. Usually verified within 1-2 hours.
                        </div>
                      )}
                      {isRejected && (
                        <button
                          type="button"
                          className="rb-btn-card-reapply"
                          onClick={() => {
                            setBankName(item.bank_name || "");
                            setAccountHolder(item.account_holder || "");
                            setIfsc(item.ifsc || "");
                            setAccountNumber(item.account_number || "");
                            setConfirmAccount(item.account_number || "");
                            setSubmitBankModalOpen(true);
                          }}
                        >
                          🔄 Re-Submit Bank Account
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          USER TAB 2: LOAD FUNDS / DEPOSITS (USER)
      ══════════════════════════════════════════════════════════════ */}
      {!isAdmin && activeTab === "load_fund" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>⚡</span> Deposit Funds &amp; Wallet Load Request
              </h2>
              <p className="rb-card-sub">
                Submit your NEFT / RTGS / IMPS deposit details using an approved whitelisted bank account for admin wallet credit.
              </p>
            </div>
          </div>

          {approvedBanks.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">⚠️</span>
              <h3>No Approved Bank Accounts Found</h3>
              <p>
                You must have at least one <strong>Admin-Approved Whitelisted Bank Account</strong> before you can submit deposit/load amount requests.
              </p>
              <button
                type="button"
                className="rb-action-btn rb-btn-primary mt-3"
                onClick={() => setSubmitBankModalOpen(true)}
              >
                ➕ Submit Bank Account For Approval
              </button>
            </div>
          ) : (
            <div className="rb-load-layout">
              {/* Load Amount Form */}
              <form onSubmit={handleSubmitLoadAmount} className="rb-load-form">
                <h3 className="rb-sub-heading">1. Select Source (Deposited From) &amp; Destination (Deposited To) Bank Accounts</h3>

                <div className="rb-grid-2">
                  {/* Whitelisted Bank Account Dropdown (Deposited From - FIRST) */}
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Deposited From (Your Whitelisted Bank) <span className="text-danger">*</span>
                    </label>
                    <select
                      className="rb-form-control"
                      value={loadBankId}
                      onChange={(e) => setLoadBankId(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Your Whitelisted Account ({approvedBanks.length} approved) --</option>
                      {approvedBanks.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.bank_name} — {b.account_number} ({b.account_holder})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Provider Bank Account Dropdown (Deposited To - SECOND) */}
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Deposit To (Provider Bank Account) <span className="text-danger">*</span>
                    </label>
                    <select
                      className="rb-form-control"
                      value={providerAccount}
                      onChange={(e) => setProviderAccount(e.target.value)}
                      required
                    >
                      <option value="">-- Choose Provider Account ({providerAccounts.length} available) --</option>
                      {providerAccounts.map((acc, idx) => {
                        const accVal = String(acc.id ?? acc.account_number ?? acc.account ?? idx);
                        const bName = acc.bank_name || acc.bankname || acc.bank || "Bank";
                        const accNo = acc.account_number || acc.account || acc.acc_no || "";
                        const holder = acc.account_holder || acc.account_holder_name || acc.holder_name || acc.name || "";
                        const ifscVal = acc.ifsc || acc.ifsc_code || acc.IFSC_Code || "";
                        return (
                          <option key={accVal} value={accVal}>
                            {bName} — {accNo} {holder ? `(${holder})` : ""}{ifscVal ? ` [${ifscVal}]` : ""}
                          </option>
                        );
                      })}
                    </select>
                    {loadingProviderAccounts && (
                      <small className="text-muted mt-1 d-block">⏳ Loading provider accounts...</small>
                    )}
                  </div>
                </div>

                {/* Selected Source Whitelisted Bank Detail Card */}
                {(() => {
                  const selectedBank = approvedBanks.find(
                    (b) => String(b.id) === String(loadBankId)
                  );
                  if (!selectedBank) return null;

                  return (
                    <div style={{
                      background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "10px",
                      padding: "12px 16px", marginTop: "10px", marginBottom: "6px",
                      display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center", justifyContent: "space-between"
                    }}>
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          👤 Your Source Bank (Deposited From)
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                          {selectedBank.bank_name} &bull; <span style={{ color: "#2563eb" }}>{selectedBank.account_holder}</span>
                        </div>
                        <div style={{ fontSize: "12.5px", color: "#334155", marginTop: "2px" }}>
                          <strong>A/C No:</strong> <code style={{ color: "#0f172a", fontWeight: 700 }}>{selectedBank.account_number}</code>
                          &nbsp;&nbsp;|&nbsp;&nbsp;
                          <strong>IFSC:</strong> <code style={{ color: "#0f172a", fontWeight: 700 }}>{selectedBank.ifsc}</code>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {selectedBank.account_number && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            style={{ fontSize: "12px", padding: "4px 10px" }}
                            onClick={() => {
                              navigator.clipboard.writeText(selectedBank.account_number);
                              ShowToast("Account Number copied!", "success");
                            }}
                          >
                            📋 Copy A/C
                          </button>
                        )}
                        {selectedBank.ifsc && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            style={{ fontSize: "12px", padding: "4px 10px" }}
                            onClick={() => {
                              navigator.clipboard.writeText(selectedBank.ifsc);
                              ShowToast("IFSC Code copied!", "success");
                            }}
                          >
                            📋 Copy IFSC
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Selected Provider Bank Detail Card */}
                {(() => {
                  const selectedProv = providerAccounts.find(
                    (a) => String(a.id ?? a.account_number ?? a.account ?? "") === String(providerAccount) ||
                           String(a.account_number ?? a.account ?? "") === String(providerAccount)
                  );
                  if (!selectedProv) return null;
                  const bName = selectedProv.bank_name || selectedProv.bankname || selectedProv.bank || "Bank";
                  const accNo = selectedProv.account_number || selectedProv.account || selectedProv.acc_no || "";
                  const holder = selectedProv.account_holder || selectedProv.account_holder_name || selectedProv.holder_name || selectedProv.name || "Company";
                  const ifscVal = selectedProv.ifsc || selectedProv.ifsc_code || selectedProv.IFSC_Code || "";
                  const branch = selectedProv.branch || selectedProv.branch_name || "";
                  const upi = selectedProv.upi_id || selectedProv.upi || "";

                  return (
                    <div style={{
                      background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "10px",
                      padding: "12px 16px", marginTop: "10px", marginBottom: "6px",
                      display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center", justifyContent: "space-between"
                    }}>
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                          🏢 Official Company Receiving Bank (Deposited To)
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a", marginTop: "2px" }}>
                          {bName} &bull; <span style={{ color: "#15803d" }}>{holder}</span>
                        </div>
                        <div style={{ fontSize: "12.5px", color: "#334155", marginTop: "2px" }}>
                          <strong>A/C No:</strong> <code style={{ color: "#0f172a", fontWeight: 700 }}>{accNo}</code>
                          &nbsp;&nbsp;|&nbsp;&nbsp;
                          <strong>IFSC:</strong> <code style={{ color: "#0f172a", fontWeight: 700 }}>{ifscVal}</code>
                          {branch && <>&nbsp;&nbsp;|&nbsp;&nbsp;<strong>Branch:</strong> {branch}</>}
                          {upi && <>&nbsp;&nbsp;|&nbsp;&nbsp;<strong>UPI ID:</strong> <code>{upi}</code></>}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {accNo && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            style={{ fontSize: "12px", padding: "4px 10px" }}
                            onClick={() => {
                              navigator.clipboard.writeText(accNo);
                              ShowToast("Account Number copied!", "success");
                            }}
                          >
                            📋 Copy A/C
                          </button>
                        )}
                        {ifscVal && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            style={{ fontSize: "12px", padding: "4px 10px" }}
                            onClick={() => {
                              navigator.clipboard.writeText(ifscVal);
                              ShowToast("IFSC Code copied!", "success");
                            }}
                          >
                            📋 Copy IFSC
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <h3 className="rb-sub-heading">2. Deposit &amp; Wallet Destination</h3>

                <div className="rb-grid-2">
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Target Wallet <span className="text-danger">*</span>
                    </label>
                    <select
                      className="rb-form-control"
                      value={loadWalletType}
                      onChange={(e) => setLoadWalletType(e.target.value)}
                      required
                    >
                      {WALLET_TYPES.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.icon} {w.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Deposit Amount (₹) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="rb-form-control rb-amount-inp"
                      placeholder="e.g. 50000"
                      value={loadAmount}
                      onChange={(e) => setLoadAmount(e.target.value)}
                      min="100"
                      step="1"
                      required
                    />
                  </div>
                </div>

                {/* Amount presets */}
                <div className="rb-preset-row">
                  {PRESET_AMOUNTS.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className="rb-preset-btn"
                      onClick={() => setLoadAmount(String(amt))}
                    >
                      + ₹{amt.toLocaleString("en-IN")}
                    </button>
                  ))}
                </div>

                <div className="rb-grid-2 mt-3">
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Bank UTR / Reference Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="rb-form-control rb-mono-inp"
                      placeholder="e.g. UTR2026092212345"
                      value={loadUtr}
                      onChange={(e) => setLoadUtr(e.target.value.toUpperCase())}
                      required
                    />
                  </div>

                  <div className="rb-form-group">
                    <label className="rb-form-label">Remarks / Description</label>
                    <input
                      type="text"
                      className="rb-form-control"
                      placeholder="e.g. NEFT deposit for recharge wallet top-up"
                      value={loadRemark}
                      onChange={(e) => setLoadRemark(e.target.value)}
                    />
                  </div>
                </div>

                {/* Deposit Slip Upload */}
                <div className="rb-form-group mt-3">
                  <label className="rb-form-label">
                    Upload Deposit Slip / Payment Screenshot <span className="text-danger">*</span>
                  </label>
                  <div
                    className={`rb-dropzone ${depositSlipPreview ? "rb-dropzone-active" : ""}`}
                    onClick={() => slipInputRef.current && slipInputRef.current.click()}
                  >
                    <input
                      ref={slipInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      style={{ display: "none" }}
                      onChange={handleDepositSlipSelect}
                    />

                    {depositSlipPreview ? (
                      <div className="rb-preview-box" onClick={(e) => e.stopPropagation()}>
                        <img src={depositSlipPreview} alt="Slip Preview" className="rb-preview-img" />
                        <div className="rb-preview-actions">
                          <span className="rb-file-name">{depositSlipFile?.name}</span>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              type="button"
                              className="rb-btn-preview-zoom"
                              onClick={() =>
                                setPreviewImageModal({
                                  open: true,
                                  title: "Deposit Slip Preview",
                                  url: depositSlipPreview,
                                  subtitle: depositSlipFile?.name,
                                })
                              }
                            >
                              🔍 Zoom
                            </button>
                            <button
                              type="button"
                              className="rb-btn-preview-remove"
                              onClick={handleClearSlip}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rb-dropzone-prompt">
                        <span className="rb-upload-icon">📸</span>
                        <div className="rb-upload-text">
                          <strong>Click to browse</strong> or drag deposit slip / screenshot
                        </div>
                        <span className="rb-upload-sub">Supports JPG, PNG (Max 5MB)</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rb-form-actions">
                  <button
                    type="submit"
                    className="rb-action-btn rb-btn-primary"
                    disabled={submittingLoad}
                  >
                    {submittingLoad ? (
                      <>
                        <span className="rb-spin">⚙️</span> Submitting Load Request...
                      </>
                    ) : (
                      "🚀 Submit Deposit Request"
                    )}
                  </button>
                </div>

                <div className="mt-4 pt-3 border-top d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <span className="text-muted fs-13">
                    Looking for previous deposit requests status and receipts?
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                    onClick={() => {
                      setActiveTab("load_requests");
                      fetchLoadRequests(false);
                    }}
                  >
                    <span>📜</span> View Load Requests History ({loadRequests.length}) →
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          USER TAB 3: LOAD REQUESTS (MY RECENT DEPOSIT REQUESTS)
      ══════════════════════════════════════════════════════════════ */}
      {!isAdmin && activeTab === "load_requests" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>📜</span> My Recent Load Requests
              </h2>
              <p className="rb-card-sub">
                Track the live status of all submitted deposit and wallet loading requests, review credited wallets, and inspect admin notes.
              </p>
            </div>

            <div className="rb-card-filters">
              <div className="rb-search-wrap">
                <input
                  type="text"
                  className="rb-search-inp"
                  placeholder="Search UTR, bank, A/C, amount, note..."
                  value={userLoadSearch}
                  onChange={(e) => setUserLoadSearch(e.target.value)}
                />
                <span className="rb-search-icon">🔍</span>
              </div>

              <select
                className="rb-select-filter"
                value={userLoadStatusFilter}
                onChange={(e) => setUserLoadStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses ({loadRequests.length})</option>
                <option value="pending">Pending Only ({loadRequests.filter(r => (r.status || "").toLowerCase() === "pending").length})</option>
                <option value="approved">Approved Only ({loadRequests.filter(r => (r.status || "").toLowerCase() === "approved").length})</option>
                <option value="rejected">Rejected Only ({loadRequests.filter(r => (r.status || "").toLowerCase() === "rejected").length})</option>
              </select>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredUserLoads, "my_deposit_requests", "xlsx")}
                  disabled={!filteredUserLoads.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredUserLoads, "my_deposit_requests", "csv")}
                  disabled={!filteredUserLoads.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchLoadRequests(false)}
                disabled={loadingLoads || isHardRefreshing}
                title="Hard refresh requests"
              >
                <span className={loadingLoads || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          {loadingLoads ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading deposit requests...</h3>
            </div>
          ) : filteredUserLoads.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">💵</span>
              <h3>No Deposit Requests Found</h3>
              <p>
                {loadRequests.length === 0
                  ? "You have not submitted any fund deposit requests yet. Load funds to start transacting."
                  : "No deposit requests match your current search and filter criteria."}
              </p>
              {loadRequests.length === 0 && (
                <button
                  type="button"
                  className="rb-action-btn rb-btn-primary mt-3"
                  onClick={() => {
                    setActiveTab("load_fund");
                    fetchSelfAccountList(false);
                  }}
                >
                  ⚡ Deposit Funds Now
                </button>
              )}
            </div>
          ) : (
            <div className="rb-history-list" style={{ marginTop: "16px" }}>
              {filteredUserLoads.map((req) => {
                const status = (req.status || "pending").toLowerCase();
                const isApproved = status === "approved";
                const isPending = status === "pending";
                const isRejected = status === "rejected";
                const slipImg = req.slip_url
                  ? req.slip_url.startsWith("http")
                    ? req.slip_url
                    : `${BASE_URL}${req.slip_url}`
                  : "";

                return (
                  <div key={req.id} className="rb-history-item">
                    <div className="rb-hist-top">
                      <div className="rb-hist-amt">
                        ₹{(parseFloat(req.amount) || 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className={`rb-status-badge rb-badge-${status}`}>
                          {isApproved && "● Approved"}
                          {isPending && "⏳ Pending"}
                          {isRejected && "✕ Rejected"}
                        </span>
                        {slipImg && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{ fontSize: "11px", padding: "2px 8px" }}
                            onClick={() =>
                              setPreviewImageModal({
                                open: true,
                                title: `Deposit Slip — Req #${req.id}`,
                                url: slipImg,
                                subtitle: `Amount: ₹${req.amount} | UTR: ${req.utr_number}`,
                              })
                            }
                          >
                            📸 Slip
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="rb-hist-mid">
                      <div>
                        <strong>👤 Deposited From:</strong>{" "}
                        <span>
                          {req.bank_name || "My Bank"}
                          {req.account_number ? ` (${req.account_number})` : ""}
                        </span>
                      </div>
                      <div className="mt-1">
                        <strong className="text-dark">🏛️ Deposited To:</strong>{" "}
                        <span>
                          {req.provider_bank_name || "Company Account"}
                          {req.provider_account_number ? ` — A/C: ${req.provider_account_number}` : ""}
                          {req.provider_ifsc ? ` [IFSC: ${req.provider_ifsc}]` : ""}
                          {req.provider_account_holder ? ` (${req.provider_account_holder})` : ""}
                        </span>
                      </div>
                      <div className="mt-1 d-flex flex-wrap gap-3 align-items-center">
                        <div className="rb-info-val-wrap">
                          <span><strong>UTR:</strong> {req.utr_number}</span>
                          <button
                            type="button"
                            className="rb-btn-copy"
                            onClick={() => handleCopy(req.utr_number, "UTR Number")}
                            title="Copy UTR"
                          >
                            📋
                          </button>
                        </div>
                        <span>
                          <strong>Wallet:</strong>{" "}
                          {req.wallet_type === "recharge_wallet" ? "⚡ Recharge Wallet" : "💼 Main Wallet"}
                        </span>
                      </div>
                    </div>

                    {req.remark && (
                      <div className="rb-hist-remark">
                        <strong>Note:</strong> {req.remark}
                      </div>
                    )}

                    {req.rejection_reason && (
                      <div className="rb-hist-rej">
                        <strong>Rejection Reason:</strong> {req.rejection_reason}
                      </div>
                    )}

                    <div className="rb-hist-date">
                      <span>Created: {req.created_at || "Recent"}</span>
                      {req.reviewed_at && (
                        <span className="ms-3 text-primary fw-medium">
                          • Reviewed: {req.reviewed_at}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          USER TAB: PROVIDER / COMPANY BANK ACCOUNTS (PROVIDER LIST)
      ══════════════════════════════════════════════════════════════ */}
      {!isAdmin && activeTab === "provider_banks" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>🏢</span> Company &amp; Provider Bank Accounts
              </h2>
              <p className="rb-card-sub">
                Official company bank accounts for direct NEFT / RTGS / IMPS deposits and load wallet requests.
              </p>
            </div>

            <div className="rb-card-filters">
              <div className="rb-search-wrap">
                <input
                  type="text"
                  className="rb-search-inp"
                  placeholder="Search provider bank, A/C, IFSC, holder..."
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                />
                <span className="rb-search-icon">🔍</span>
              </div>

              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredProviderAccounts, "company_provider_accounts", "xlsx")}
                  disabled={!filteredProviderAccounts.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(filteredProviderAccounts, "company_provider_accounts", "csv")}
                  disabled={!filteredProviderAccounts.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchSelfAccountList(false)}
                disabled={loadingProviderAccounts || isHardRefreshing}
                title="Hard refresh provider accounts"
              >
                <span className={loadingProviderAccounts || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          {loadingProviderAccounts ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading provider bank accounts...</h3>
            </div>
          ) : filteredProviderAccounts.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">🏢</span>
              <h3>No Provider Accounts Found</h3>
              <p>
                {providerAccounts.length === 0
                  ? "No company bank accounts are currently configured in the system."
                  : "No provider accounts match your search filter."}
              </p>
            </div>
          ) : (
            <div className="rb-banks-grid">
              {filteredProviderAccounts.map((item, idx) => {
                const accVal = item.id || item.account_number || idx;
                const qrUrl = item.qr_code || item.qr_code_url || item.qr_image || "";
                const isQrAvailable = Boolean(qrUrl);

                return (
                  <div key={accVal} className="rb-bank-card rb-border-approved">
                    <div className="rb-bank-card-top">
                      <div className="rb-bank-avatar" style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)", color: "#fff" }}>
                        <span>🏛️</span>
                      </div>
                      <div className="rb-bank-title-group">
                        <h3 className="rb-bank-name">{item.bank_name || "Company Provider Bank"}</h3>
                        <span className="rb-holder-name">
                          👤 {item.account_holder || "Official Company Account"}
                        </span>
                      </div>
                      <span className="rb-status-badge rb-badge-approved">
                        ● Active Deposit A/C
                      </span>
                    </div>

                    <div className="rb-bank-card-body">
                      <div className="rb-info-row">
                        <span className="rb-info-lbl">Account Number</span>
                        <div className="rb-info-val-wrap">
                          <span className="rb-info-mono">{item.account_number}</span>
                          <button
                            type="button"
                            className="rb-btn-copy"
                            onClick={() => handleCopy(item.account_number, "Provider Account Number")}
                            title="Copy Account Number"
                          >
                            📋
                          </button>
                        </div>
                      </div>

                      <div className="rb-info-row">
                        <span className="rb-info-lbl">IFSC Code</span>
                        <div className="rb-info-val-wrap">
                          <span className="rb-info-mono">{item.ifsc}</span>
                          <button
                            type="button"
                            className="rb-btn-copy"
                            onClick={() => handleCopy(item.ifsc, "Provider IFSC Code")}
                            title="Copy IFSC"
                          >
                            📋
                          </button>
                        </div>
                      </div>

                      {item.account_holder && (
                        <div className="rb-info-row">
                          <span className="rb-info-lbl">Account Holder</span>
                          <span className="fw-medium text-dark">{item.account_holder}</span>
                        </div>
                      )}

                      {item.branch && (
                        <div className="rb-info-row">
                          <span className="rb-info-lbl">Branch</span>
                          <span className="text-muted">{item.branch}</span>
                        </div>
                      )}

                      {item.upi_id && (
                        <div className="rb-info-row">
                          <span className="rb-info-lbl">UPI ID</span>
                          <div className="rb-info-val-wrap">
                            <span className="rb-info-mono">{item.upi_id}</span>
                            <button
                              type="button"
                              className="rb-btn-copy"
                              onClick={() => handleCopy(item.upi_id, "UPI ID")}
                              title="Copy UPI ID"
                            >
                              📋
                            </button>
                          </div>
                        </div>
                      )}

                      {isQrAvailable && (
                        <div className="mt-2 text-center p-2 rounded bg-light border">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
                            onClick={() =>
                              setPreviewImageModal({
                                open: true,
                                title: `Deposit QR — ${item.bank_name}`,
                                url: qrUrl.startsWith("http") ? qrUrl : `${BASE_URL}${qrUrl}`,
                                subtitle: `${item.account_holder} | A/C: ${item.account_number} | IFSC: ${item.ifsc}`,
                              })
                            }
                          >
                            📱 View Payment QR Code
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          USER TAB 4: MY WALLET HOLDS (USER)
      ══════════════════════════════════════════════════════════════ */}
      {!isAdmin && activeTab === "my_holds" && (
        <div className="rb-tab-content-card">
          <div className="rb-card-header">
            <div>
              <h2 className="rb-card-title">
                <span>🔒</span> My Wallet Holds &amp; Security Reserves
              </h2>
              <p className="rb-card-sub">
                View currently held balances and historical hold/release transactions on your wallet accounts.
              </p>
            </div>

            <div className="d-inline-flex flex-row align-items-center gap-2" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
              <div className="d-inline-flex flex-row align-items-center gap-1" style={{ display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap' }}>
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(userHoldList, "my_wallet_holds", "xlsx")}
                  disabled={!userHoldList.length}
                  title="Export as Excel"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileExcel className="fs-6 me-1" /> Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 text-nowrap"
                  onClick={() => handleExportBankData(userHoldList, "my_wallet_holds", "csv")}
                  disabled={!userHoldList.length}
                  title="Export as CSV"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <FaFileCsv className="fs-6 me-1" /> CSV
                </button>
              </div>
              <button
                type="button"
                className="rb-btn-refresh"
                onClick={() => fetchUserHolds(false)}
                disabled={loadingUserHolds || isHardRefreshing}
                title="Hard refresh holds"
              >
                <span className={loadingUserHolds || isHardRefreshing ? "rb-spin" : ""}>🔄</span>
              </button>
            </div>
          </div>

          <div className="rb-hold-summary-banner">
            <div className="rb-hold-summary-left">
              <span className="rb-hold-badge-label">Total Held Wallet Balance:</span>
              <strong className="rb-hold-badge-value">₹{(userCurrentHold || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
            </div>
            <div className="text-muted fs-12">
              ℹ️ Held amounts are reserved temporarily until transaction verifications complete.
            </div>
          </div>

          {loadingUserHolds ? (
            <div className="rb-empty-state">
              <span className="rb-spin rb-big-icon">⏳</span>
              <h3>Loading wallet holds...</h3>
            </div>
          ) : userHoldList.length === 0 ? (
            <div className="rb-empty-state">
              <span className="rb-big-icon">🎉</span>
              <h3>No Wallet Holds</h3>
              <p>Your wallet accounts are fully active with zero held balances.</p>
            </div>
          ) : (
            <div className="table-responsive rb-table-wrap">
              <table className="table table-hover rb-admin-table align-middle">
                <thead>
                  <tr>
                    <th>Hold ID</th>
                    <th>Wallet Type</th>
                    <th>Action</th>
                    <th>Amount</th>
                    <th>Reason</th>
                    <th>Admin / Operator</th>
                    <th>Date &amp; Time</th>
                  </tr>
                </thead>
                <tbody>
                  {userHoldList.map((h) => {
                    const isHold = (h.action || "").toLowerCase() === "hold";
                    return (
                      <tr key={h.id}>
                        <td>
                          <span className="rb-badge-id">#{h.id}</span>
                        </td>
                        <td>
                          <span className="rb-wallet-tag">
                            {h.wallet_type === "recharge_wallet" ? "⚡ Recharge Wallet" : "💼 Main Wallet"}
                          </span>
                        </td>
                        <td>
                          <span className={`rb-hold-action-badge ${isHold ? "rb-badge-hold" : "rb-badge-unhold"}`}>
                            {isHold ? "🔒 Hold Placed" : "🔓 Hold Released"}
                          </span>
                        </td>
                        <td>
                          <strong className={isHold ? "text-danger" : "text-success"}>
                            {isHold ? "- " : "+ "}₹{(parseFloat(h.amount) || 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </strong>
                        </td>
                        <td>
                          <span className="fw-medium text-dark">{h.reason || "Verification hold"}</span>
                        </td>
                        <td>
                          <span className="text-muted fs-12">{h.performed_by || "Admin"}</span>
                        </td>
                        <td className="text-muted fs-12">{h.created_at || "Recent"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL: ADMIN REVIEW BANK WHITELIST
      ══════════════════════════════════════════════════════════════ */}
      {reviewBankModal && selectedBankItem && (
        <div className="rb-modal-overlay" onClick={() => !reviewingBank && setReviewBankModal(false)}>
          <div className="rb-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-header">
              <h3 className="rb-modal-title">
                {reviewDecision === "approve" ? "✅ Approve Bank Whitelist" : "❌ Reject Bank Whitelist"}
              </h3>
              <button
                type="button"
                className="rb-modal-close"
                onClick={() => !reviewingBank && setReviewBankModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="rb-modal-body">
              <div className="rb-review-summary-card">
                <div className="rb-rev-row">
                  <span>Partner:</span> <strong>{selectedBankItem.user_name || selectedBankItem.account_holder} ({selectedBankItem.user_phone || "N/A"})</strong>
                </div>
                <div className="rb-rev-row">
                  <span>Bank:</span> <strong>{selectedBankItem.bank_name}</strong>
                </div>
                <div className="rb-rev-row">
                  <span>Account Number:</span> <strong>{selectedBankItem.account_number}</strong>
                </div>
                <div className="rb-rev-row">
                  <span>IFSC Code:</span> <strong>{selectedBankItem.ifsc}</strong>
                </div>
                <div className="rb-rev-row">
                  <span>Holder Name:</span> <strong>{selectedBankItem.account_holder}</strong>
                </div>
              </div>

              <div className="rb-form-group mt-3">
                <label className="rb-form-label">
                  Review Remark <span className="text-danger">*</span>
                </label>
                <textarea
                  className="rb-form-control"
                  rows={3}
                  value={reviewRemark}
                  onChange={(e) => setReviewRemark(e.target.value)}
                  placeholder="Enter reason or verification notes..."
                  required
                />
              </div>
            </div>

            <div className="rb-modal-footer">
              <button
                type="button"
                className="rb-btn-outline"
                onClick={() => setReviewBankModal(false)}
                disabled={reviewingBank}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`rb-action-btn ${reviewDecision === "approve" ? "rb-btn-primary" : "rb-btn-danger"}`}
                onClick={handleReviewBankSubmit}
                disabled={reviewingBank || !reviewRemark.trim()}
              >
                {reviewingBank ? (
                  <>
                    <span className="rb-spin">⚙️</span> Processing...
                  </>
                ) : reviewDecision === "approve" ? (
                  "Confirm & Whitelist Account"
                ) : (
                  "Confirm Rejection"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL: ADMIN REVIEW LOAD AMOUNT REQUEST
      ══════════════════════════════════════════════════════════════ */}
      {reviewLoadModal && selectedLoadItem && (
        <div className="rb-modal-overlay" onClick={() => !reviewingLoad && setReviewLoadModal(false)}>
          <div className="rb-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-header">
              <h3 className="rb-modal-title">
                {reviewLoadDecision === "approve" ? "✅ Credit Wallet & Approve Load" : "❌ Reject Deposit Request"}
              </h3>
              <button
                type="button"
                className="rb-modal-close"
                onClick={() => !reviewingLoad && setReviewLoadModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="rb-modal-body">
              <div className="rb-review-summary-card">
                <div className="rb-rev-row">
                  <span>User / Partner:</span> <strong>{selectedLoadItem.user_name || "Partner"} ({selectedLoadItem.user_phone || "N/A"})</strong>
                </div>
                <div className="rb-rev-row">
                  <span>Deposit Amount:</span>
                  <strong className="rb-amt-highlight">
                    ₹{(parseFloat(selectedLoadItem.amount) || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}
                  </strong>
                </div>
                <div className="rb-rev-row">
                  <span>Target Wallet:</span> <strong>{selectedLoadItem.wallet_type === "recharge_wallet" ? "⚡ Recharge Wallet" : "💼 Main Wallet"}</strong>
                </div>
                <div className="rb-rev-row">
                  <span>Deposited From (User A/C):</span>{" "}
                  <strong>
                    👤 {selectedLoadItem.bank_name || "User Bank"} {selectedLoadItem.account_number ? `(${selectedLoadItem.account_number})` : ""}
                  </strong>
                </div>
                <div className="rb-rev-row">
                  <span>Deposited To (Company A/C):</span>{" "}
                  <strong>
                    🏛️ {selectedLoadItem.provider_bank_name || "Company Account"}
                    {selectedLoadItem.provider_account_number ? ` — ${selectedLoadItem.provider_account_number}` : ""}
                    {selectedLoadItem.provider_ifsc ? ` (${selectedLoadItem.provider_ifsc})` : ""}
                    {selectedLoadItem.provider_account_holder ? ` [${selectedLoadItem.provider_account_holder}]` : ""}
                  </strong>
                </div>
                <div className="rb-rev-row">
                  <span>Bank UTR:</span> <strong>{selectedLoadItem.utr_number}</strong>
                </div>
                {selectedLoadItem.remark && (
                  <div className="rb-rev-row">
                    <span>User Remark:</span> <em>{selectedLoadItem.remark}</em>
                  </div>
                )}
                <div className="rb-rev-row">
                  <span>Submitted Date:</span> <span>{selectedLoadItem.created_at || "Recent"}</span>
                </div>
              </div>

              <div className="rb-form-group mt-3">
                <label className="rb-form-label">
                  {reviewLoadDecision === "approve" ? "Approval Remark" : "Rejection Reason"} <span className="text-danger">*</span>
                </label>
                <textarea
                  className="rb-form-control"
                  rows={3}
                  value={reviewLoadRemark}
                  onChange={(e) => setReviewLoadRemark(e.target.value)}
                  placeholder={reviewLoadDecision === "approve" ? "e.g. UTR verified in bank statement" : "e.g. UTR not found in bank statement"}
                  required
                />
              </div>
            </div>

            <div className="rb-modal-footer">
              <button
                type="button"
                className="rb-btn-outline"
                onClick={() => setReviewLoadModal(false)}
                disabled={reviewingLoad}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`rb-action-btn ${reviewLoadDecision === "approve" ? "rb-btn-primary" : "rb-btn-danger"}`}
                onClick={handleReviewLoadSubmit}
                disabled={reviewingLoad || !reviewLoadRemark.trim()}
              >
                {reviewingLoad ? (
                  <>
                    <span className="rb-spin">⚙️</span> Processing...
                  </>
                ) : reviewLoadDecision === "approve" ? (
                  `Credit ₹${selectedLoadItem.amount} to Wallet`
                ) : (
                  "Reject Load Request"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL: ADMIN WALLET HOLD / RELEASE ACTION
      ══════════════════════════════════════════════════════════════ */}
      {holdActionModal.open && (
        <div className="rb-modal-overlay" onClick={() => !submittingHoldAction && setHoldActionModal((p) => ({ ...p, open: false }))}>
          <div className="rb-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-header">
              <h3 className="rb-modal-title">
                {holdActionModal.type === "hold" ? "🔒 Place Hold on Wallet Balance" : "🔓 Release Held Wallet Amount"}
              </h3>
              <button
                type="button"
                className="rb-modal-close"
                onClick={() => !submittingHoldAction && setHoldActionModal((p) => ({ ...p, open: false }))}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleHoldActionSubmit}>
              <div className="rb-modal-body">
                <div className="rb-grid-2">
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      User ID (Target) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="rb-form-control rb-mono-inp"
                      placeholder="e.g. 452"
                      value={holdActionModal.userId}
                      onChange={(e) =>
                        setHoldActionModal((prev) => ({
                          ...prev,
                          userId: e.target.value.replace(/\D/g, ""),
                        }))
                      }
                      required
                    />
                  </div>

                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Wallet Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="rb-form-control"
                      value={holdActionModal.walletType}
                      onChange={(e) =>
                        setHoldActionModal((prev) => ({
                          ...prev,
                          walletType: e.target.value,
                        }))
                      }
                      required
                    >
                      <option value="main_wallet">💼 Main Wallet</option>
                      <option value="recharge_wallet">⚡ Recharge Wallet</option>
                    </select>
                  </div>
                </div>

                <div className="rb-form-group">
                  <label className="rb-form-label">
                    Amount to {holdActionModal.type === "hold" ? "Hold" : "Release"} (₹) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="rb-form-control rb-amount-inp"
                    placeholder="e.g. 10000"
                    value={holdActionModal.amount}
                    onChange={(e) =>
                      setHoldActionModal((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    min="1"
                    step="1"
                    required
                  />
                </div>

                <div className="rb-form-group">
                  <label className="rb-form-label">
                    Reason / Justification <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="rb-form-control"
                    rows={3}
                    placeholder={
                      holdActionModal.type === "hold"
                        ? "e.g. UTR under bank verification"
                        : "e.g. Bank confirmed deposit is genuine"
                    }
                    value={holdActionModal.reason}
                    onChange={(e) =>
                      setHoldActionModal((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
              </div>

              <div className="rb-modal-footer">
                <button
                  type="button"
                  className="rb-btn-outline"
                  onClick={() => setHoldActionModal((p) => ({ ...p, open: false }))}
                  disabled={submittingHoldAction}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`rb-action-btn ${holdActionModal.type === "hold" ? "rb-btn-danger" : "rb-btn-success"}`}
                  disabled={submittingHoldAction}
                >
                  {submittingHoldAction ? (
                    <>
                      <span className="rb-spin">⚙️</span> Processing...
                    </>
                  ) : holdActionModal.type === "hold" ? (
                    "🔒 Confirm Wallet Hold"
                  ) : (
                    "🔓 Confirm Release to Wallet"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL: SUBMIT BANK ACCOUNT FOR WHITELISTING
      ══════════════════════════════════════════════════════════════ */}
      {submitBankModalOpen && (
        <div
          className="rb-modal-overlay"
          onClick={() => !submittingBank && setSubmitBankModalOpen(false)}
        >
          <div
            className="rb-modal-content"
            style={{ maxWidth: "820px", width: "95%", maxHeight: "90vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="rb-modal-header">
              <div>
                <h3 className="rb-modal-title">
                  <span>➕</span> Submit Bank Account for Whitelisting
                </h3>
                <span className="text-muted fs-12">
                  Provide accurate banking details and upload a cancelled cheque proof for admin verification.
                </span>
              </div>
              <button
                type="button"
                className="rb-modal-close"
                onClick={() => !submittingBank && setSubmitBankModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="rb-modal-body">
              <form onSubmit={handleSubmitBank} className="rb-form-main">
                {/* Popular Banks Quick Select */}
                <div className="rb-form-group">
                  <label className="rb-form-label">
                    Popular Banks (Quick Select)
                  </label>
                  <div className="rb-popular-banks-grid">
                    {POPULAR_BANKS.map((b) => (
                      <button
                        key={b.code}
                        type="button"
                        className={`rb-bank-chip ${bankName === b.name ? "rb-chip-selected" : ""}`}
                        onClick={() => setBankName(b.name)}
                      >
                        <span className="rb-chip-icon">{b.icon}</span>
                        <span className="rb-chip-name">{b.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rb-grid-2">
                  {/* Bank Name */}
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Bank Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="rb-form-control"
                      placeholder="e.g. HDFC Bank, State Bank of India"
                      value={bankName}
                      onChange={(e) => setBankName(e.target.value)}
                      required
                    />
                  </div>

                  {/* Account Holder Name */}
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Account Holder Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="rb-form-control"
                      placeholder="As printed on cheque / passbook"
                      value={accountHolder}
                      onChange={(e) => setAccountHolder(e.target.value)}
                      required
                    />
                    <span className="rb-input-hint">Must match your verified company or KYC name.</span>
                  </div>
                </div>

                <div className="rb-grid-2">
                  {/* Account Number */}
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Account Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="rb-form-control rb-mono-inp"
                      placeholder="Enter full bank account number"
                      value={accountNumber}
                      onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                      maxLength={20}
                      required
                    />
                  </div>

                  {/* Confirm Account Number */}
                  <div className="rb-form-group">
                    <label className="rb-form-label">
                      Confirm Account Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`rb-form-control rb-mono-inp ${confirmAccount && confirmAccount !== accountNumber ? "is-invalid" : ""}`}
                      placeholder="Re-enter account number"
                      value={confirmAccount}
                      onChange={(e) => setConfirmAccount(e.target.value.replace(/\D/g, ""))}
                      maxLength={20}
                      required
                    />
                    {confirmAccount && confirmAccount !== accountNumber && (
                      <span className="rb-err-hint">⚠️ Account numbers do not match!</span>
                    )}
                  </div>
                </div>

                {/* IFSC Code */}
                <div className="rb-form-group">
                  <label className="rb-form-label">
                    IFSC Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="rb-form-control rb-mono-inp"
                    placeholder="e.g. HDFC0001234"
                    value={ifsc}
                    onChange={(e) => setIfsc(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                    maxLength={11}
                    required
                  />
                  <span className="rb-input-hint">11-character alphanumeric code without spaces.</span>
                </div>

                {/* Cancelled Cheque Upload */}
                <div className="rb-form-group">
                  <label className="rb-form-label">
                    Upload Cancelled Cheque / Passbook Image <span className="text-danger">*</span>
                  </label>
                  <div
                    className={`rb-dropzone ${chequePreview ? "rb-dropzone-active" : ""}`}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/jpg"
                      style={{ display: "none" }}
                      onChange={handleChequeFileSelect}
                    />

                    {chequePreview ? (
                      <div className="rb-preview-box" onClick={(e) => e.stopPropagation()}>
                        <img src={chequePreview} alt="Cheque Preview" className="rb-preview-img" />
                        <div className="rb-preview-actions">
                          <span className="rb-file-name">{chequeFile?.name}</span>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <button
                              type="button"
                              className="rb-btn-preview-zoom"
                              onClick={() =>
                                setPreviewImageModal({
                                  open: true,
                                  title: "Cancelled Cheque Preview",
                                  url: chequePreview,
                                  subtitle: chequeFile?.name,
                                })
                              }
                            >
                              🔍 Zoom
                            </button>
                            <button
                              type="button"
                              className="rb-btn-preview-remove"
                              onClick={handleClearCheque}
                            >
                              ✕ Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rb-dropzone-prompt">
                        <span className="rb-upload-icon">📄</span>
                        <div className="rb-upload-text">
                          <strong>Click to browse</strong> or drag and drop your cancelled cheque
                        </div>
                        <span className="rb-upload-sub">Supports JPG, PNG (Max 5MB)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Guidelines */}
                <div className="p-3 bg-light rounded border mb-3">
                  <div style={{ fontWeight: 700, fontSize: "13px", color: "#1e293b", marginBottom: "4px" }}>
                    📋 Verification Checklist:
                  </div>
                  <ul style={{ fontSize: "12px", color: "#475569", paddingLeft: "18px", margin: 0 }}>
                    <li>Ensure account holder name, account number, and IFSC are clearly legible on the cheque.</li>
                    <li>Account holder name must match your KYC registered partner name.</li>
                    <li>Cross stamp the cheque by writing <code>CANCELLED</code> between two parallel lines.</li>
                  </ul>
                </div>

                {/* Submit Buttons */}
                <div className="rb-modal-footer" style={{ padding: "12px 0 0 0", borderTop: "1px solid #e2e8f0" }}>
                  <button
                    type="button"
                    className="rb-btn-outline"
                    onClick={() => setSubmitBankModalOpen(false)}
                    disabled={submittingBank}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="rb-action-btn rb-btn-primary"
                    disabled={submittingBank}
                  >
                    {submittingBank ? (
                      <>
                        <span className="rb-spin">⚙️</span> Submitting for Whitelisting...
                      </>
                    ) : (
                      "🚀 Submit Bank Account"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL: HIGH RESOLUTION IMAGE PREVIEW (CHEQUE / SLIP)
      ══════════════════════════════════════════════════════════════ */}
      {previewImageModal.open && (
        <div
          className="rb-modal-overlay rb-image-modal-overlay"
          onClick={() => setPreviewImageModal({ open: false, title: "", url: "", subtitle: "" })}
        >
          <div className="rb-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="rb-modal-header">
              <div>
                <h3 className="rb-modal-title">{previewImageModal.title}</h3>
                {previewImageModal.subtitle && (
                  <span className="text-muted fs-12">{previewImageModal.subtitle}</span>
                )}
              </div>
              <button
                type="button"
                className="rb-modal-close"
                onClick={() => setPreviewImageModal({ open: false, title: "", url: "", subtitle: "" })}
              >
                ✕
              </button>
            </div>

            <div className="rb-image-modal-body">
              <img src={previewImageModal.url} alt="Document Proof" className="rb-full-preview-img" />
            </div>

            <div className="rb-modal-footer">
              <a
                href={previewImageModal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rb-action-btn rb-btn-primary"
              >
                🔗 Open in Full Window
              </a>
              <button
                type="button"
                className="rb-btn-outline"
                onClick={() => setPreviewImageModal({ open: false, title: "", url: "", subtitle: "" })}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
